// ─────────────────────────────────────────────────────────────
//  GEOBAJA · Design System v3 — "base atemporal, personalidad en los detalles"
//  Fuente de verdad de la capa visual. Reglas de uso: DESIGN.md (raíz del repo).
//  Tipografía: Geist (display/titulares) + Inter (UI) + Geist Mono, en index.html.
//  Al importarse, este módulo inyecta la hoja base `gb-base` (estados, focus,
//  utilidades numéricas y responsivas, keyframes). Este archivo se lee COMPLETO:
//  los logos en base64 viven en src/logos.js (se re-exporta LOGO_SRC por
//  compatibilidad con quien ya lo pedía aquí).
// ─────────────────────────────────────────────────────────────

import { LOGO_SRC } from './logos.js';

// ══════════════════════════════════════════════
//  C — COLOR · paleta con roles semánticos
//  Regla AA: todo par texto/fondo de esta tabla cumple 4.5:1 (texto) o 3:1
//  (gráficos). `mark` y `textDisabled` son roles gráficos/inertes, no de lectura.
// ══════════════════════════════════════════════
// ── Helpers de derivación de color (puros) — Obj. A: que los tokens que hoy se
//    copian "a mano" sigan a su color base (regla anti-"paleta zombi", DESIGN.md §1).
const hexToRgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const mix = (a, b, t) => {
  const A = parseInt(a.slice(1), 16), B = parseInt(b.slice(1), 16);
  const ch = s => ((A >> s) & 255) + (((B >> s) & 255) - ((A >> s) & 255)) * t;
  const r = Math.round(ch(16)), g = Math.round(ch(8)), bl = Math.round(ch(0));
  return "#" + [r, g, bl].map(v => v.toString(16).padStart(2, "0")).join("").toUpperCase();
};

// ── LA PALETA DE DÍA: los valores de siempre. `C` (abajo) la expone como
//    variables CSS donde la noche cambia algo, y como valor fijo donde no. ──
const DIA = {
  // Brand — azul acero (instrumento)
  primary:      "#23476F",
  primaryDark:  "#1B3A5C",
  primaryDeep:  "#142C46",
  primaryLight: "#EAF0F6",
  primaryMid:   "#BACBDE",
  onPrimary:    "#FFFFFF",   // texto/iconos sobre rellenos primary u oscuros
  // Blanco atenuado sobre rellenos primary/oscuros (texto secundario, bordes
  // de cita, metadatos). Evita rgba(255,255,255,x) sueltos en los módulos.
  onPrimaryStrong: "rgba(255,255,255,0.90)", // título/autor citado
  onPrimarySoft:   "rgba(255,255,255,0.82)", // preview/texto secundario
  onPrimaryMuted:  "rgba(255,255,255,0.70)", // hora/metadatos
  onPrimaryFaint:  "rgba(255,255,255,0.62)", // "editado", marcas tenues
  onPrimaryBorder: "rgba(255,255,255,0.55)", // borde de cita en burbuja propia

  // Accent — teal de acción. Hasta 2026-09-09 fue también el color de foco;
  // hoy el foco es `C.focus` (azul de marca, un solo tinte, decisión del dueño
  // sobre el lienzo "Catálogo de conceptos").
  accent:       "#0E7C8B",
  accentHover:  "#0A6675",
  accentLight:  "#E2F2F4",
  accentBorder: "#B7DCE2",

  // Firma — ámbar de marca (rol gráfico: nav activa, sellos; no texto pequeño)
  mark:         "#C8870F",
  markDeep:     "#9C5D00",   // texto naranja sobre markLight/blanco (AA 4.6:1)
  markLight:    "#FBF1DD",
  markBorder:   "#EBD7A8",

  // Estados — saturación baja, washes cálidos. "Vencido" vive en ámbar-terracota;
  // el rojo de verdad es `critical`, reservado a lo crítico/destructivo.
  danger:       "#9E4E37",
  dangerLight:  "#F6EBE2",
  dangerBorder: "#E0C2AE",
  // overdue/overdueLight/overdueBorder = danger* (alias post-C; ver bloque abajo)
  critical:     "#B42318",
  criticalLight:"#FBEAE7",
  criticalBorder:"#EFB3AA",
  warning:      "#8A5F0F",
  warningLight: "#FBF4E6",
  warningBorder:"#E8CF94",
  success:      "#2E6B4F",
  successLight: "#EBF4EF",
  successBorder:"#B4D4C2",
  purple:       "#574FB0",
  purpleLight:  "#ECEAFB",
  purpleBorder: "#CFCBEE",

  // Superficies — fondo ≠ blanco; las tarjetas sí (jerarquía sin borde)
  bg:            "#FBFCFD",
  surface:       "#FFFFFF",
  surfaceSunken: "#F4F6F9",  // pozos/insets dentro de tarjetas
  border:        "#E8ECF2",
  borderLight:   "#F0F3F7",
  borderMid:     "#D2D9E2",

  // Texto — tinta fría
  text:         "#1B2230",
  textMed:      "#3A4658",
  textSub:      "#5E6B7E",
  textMuted:    "#66748B",
  textDisabled: "#A8B2C2",   // solo estados disabled (rol inerte, exento de AA)

  // Sidebar (navy oscuro): hoy lo usan el login, el portal del cliente y el
  // panel de descargas. El panel lateral del shell dejó el navy el 2026-09-14
  // por la "columna hundida" del lenguaje en calma (tokens panel*, más abajo).
  sidebar:      "#16243D",
  sidebarBorder:"#22324F",
  sidebarHover: "rgba(255,255,255,0.06)",
  sidebarActive:"rgba(255,255,255,0.10)",
  sidebarAccent:"#5B9BD0",
  sidebarText:  "#9FB1CC",
  sidebarTextActive: "#FFFFFF",

  // Tabla
  tableHeader:  "#F4F6F9",
  tableAlt:     "#F2F5F9",   // antes #FBFCFD (==bg → zebra invisible); ahora visible sobre bg y surface
  tableHover:   "#EEF2F7",
  tableSelected:"#DCE6F2",

  // Variantes vivas y de shell
  successVivid:      "#168552",  // chips/contadores QC; AA sobre blanco
  successVividLight: "#EBF7F0",
  // headerText = primary (alias post-C; ver bloque abajo)

  // Capas y puentes
  // Dos intensidades de velo, un solo tinte. La distinción es de FUNCIÓN, no
  // de gusto: el modal INTERRUMPE (nada detrás es operable) y el menú
  // desplegado solo TOMA EL TURNO (el contexto sigue legible, baja de plano
  // para que el menú se lea como una función y no como una hoja más).
  scrim:        "rgba(16,25,40,0.55)",  // modales y drawers: interrumpen
  scrimMenu:    "rgba(16,25,40,0.22)",  // menús desplegados: bajan el contexto
  pdfNavy:      "#2F3B52",              // espejo del INST de jsPDF para previews web de reportes
  pdfStripe:    "#F2F2F2",              // espejo del TH_BG de jsPDF: secciones sombreadas en vistas con lenguaje PDF
  pdfDark:      "#1E1E1E",              // espejo del DARK de jsPDF (texto principal del documento)
  pdfGray:      "#646464",              // espejo del GRAY de jsPDF (texto secundario)
  pdfBorder:    "#D9D9D9",              // espejo del BORDER de jsPDF (rejilla y separadores)
  pdfAlt:       "#FAFAFA",              // espejo del ALT_BG de jsPDF (alternancia de filas)
  // El navy del PDF como TINTA fuera de la hoja (el título del documento en el
  // editor de presupuestos): de día es el mismo pdfNavy; de noche se lee claro.
  navyTinta:    "#2F3B52",

  // ── Estado de DOCUMENTO (reporte que ve el cliente) ───────────────────
  //  Los colores con que se publica un veredicto de laboratorio: la página
  //  pública del QR, su vista previa interna y el análisis de situaciones.
  //  NO son los tokens de estado de la app y no deben unificarse con ellos:
  //  el cliente ya conoce estos colores de los QR impresos, y `success`/
  //  `accent`/`warning` son primos cercanos pero distintos. Lo que aquí se
  //  arregla es que existan UNA sola vez (estaban escritos a mano en tres
  //  archivos, ya divergiendo entre sí). `docBad*` sí es `critical` y se
  //  aliasa abajo en vez de duplicar el hex.
  docOk:        "#276749",   docOkLight:   "#F0FAF4",   docOkBorder:   "#A7D7B8",
  docCond:      "#0F7490",   docCondLight: "#E0F5F9",   docCondBorder: "#7CC8D8",
  docWarn:      "#92600A",   docWarnLight: "#FFFBEB",   docWarnBorder: "#F6D860",
  docNeutral:   "#4A5568",   docNeutralLight: "#F7F9FC", docNeutralBorder: "#E2E8F0",

  // ── Data-viz (v4) — secuencia categórica para charts SVG ──────────────
  //  Ordenada por máxima separación de TONO. Cada serie cumple ≥3:1 vs `surface`
  //  (AA gráfico, WCAG 1.4.11). Reusa hexes de marca donde ya existen y deriva
  //  ámbar/terracota para los huecos. La distinción entre series adyacentes es
  //  perceptual (tono), no de luminancia: 6 colores legibles sobre blanco no pueden
  //  además contrastar 3:1 entre sí (ver nota en DESIGN.md). Para estado semántico
  //  (cumple/alerta/crítico) NO usar esta secuencia — usar el mapa de estatus.
  dataViz2:     "#B26A00",   // ámbar profundo (familia mark, oscurecido para AA) — color propio
  dataViz4:     "#9E3B2E",   // terracota (familia danger, saturada) — color propio
  // dataViz1/3/5/6 = primary/accent/success/purple (alias) y gridLine/refLine
  // (derivados) se asignan tras cerrar C — regla anti-duplicado §1 (ver abajo).
  // axisLabel = textMuted (alias; se asigna tras cerrar el objeto para no duplicar el hex)

  // ── Error inline de input (v4) — terracota; antes se simulaba con `danger` ──
  errorField:       "#FBEEE9",   // wash de fondo de campo inválido
  errorFieldBorder: "#B5482F",   // borde 1px de campo inválido (≥3:1 vs surface)
};
// axisLabel: rol de label de eje. Alias semántico de textMuted; post-asignado para
// que exista UN solo valor (regla anti-"paleta zombi" de DESIGN.md §1).
DIA.axisLabel = DIA.textMuted;

// ── Objetivo A — alias y derivados post-C (un solo dueño por valor) ──────────
DIA.overdue       = DIA.danger;
DIA.overdueLight  = DIA.dangerLight;
DIA.overdueBorder = DIA.dangerBorder;
DIA.dataViz1      = DIA.primary;
DIA.dataViz3      = DIA.accent;
DIA.dataViz5      = DIA.success;
DIA.dataViz6      = DIA.purple;
DIA.headerText    = DIA.primary;
// Foco de teclado y de campo: azul de marca. UN solo dueño del color de foco
// (anillo gb-focus, outline global, resplandor de input). Antes era `accent`.
DIA.focus         = DIA.primary;
DIA.refLine       = hexToRgba(DIA.mark, 0.40);            // línea de meta — sigue a mark
DIA.gridLine      = mix(DIA.border, DIA.borderMid, 0.30);   // ≈ #E1E6ED — familia border
// Literales de gb-base tokenizados (sin cambio visual):
DIA.shine         = "rgba(255,255,255,0.20)";   // barrido de luz en rellenos (gb-shine)
DIA.skelSheen     = "rgba(255,255,255,0.65)";   // brillo del shimmer (gb-skel)
// Objetivo B — paso oscuro de critical para el hover de relleno destructivo:
DIA.criticalDark  = "#941D14";
// El rojo del documento ES `critical` (mismo hex): alias, no copia.
DIA.docBad        = DIA.critical;
DIA.docBadLight   = DIA.criticalLight;
DIA.docBadBorder  = DIA.criticalBorder;
// ── Inicio "en calma" (2026-09-12) ───────────────────────────────────────────
//  Rampa azul del dinero que fluye (sinEnviar < vigentes < transito =
//  primary): tres pasos validados con el validador de dataviz (ΔE y contraste
//  vs surface). Sin verde ni rojo: el color dice FLUJO, no alarma; ámbar y
//  terracota quedan para lo que es un problema real (vencido, observada,
//  en disputa). Los velos son los marcos del widget sobre el paisaje y las
//  etiquetas flotantes del diagrama de flujo; la jornada es la barra de
//  horario de oficina del pie.
DIA.rampa1        = "#9FB4CC";
DIA.rampa2        = "#5A7CA3";
// El badge de cada etapa en la cartera (2026-09-17, opción A de "Oscuro en calma":
// la cartera pinta lo mismo que el Inicio). Relleno lavado y texto un paso más
// hondo de la misma rampa, para que el texto cumpla AA (5.1:1 y 6.4:1).
DIA.rampa1Light   = "#EEF2F7";
DIA.rampa1Deep    = "#4A698C";
DIA.rampa2Light   = "#E3EBF5";
DIA.rampa2Deep    = "#2C5582";
DIA.widgetVelo    = "rgba(255,255,255,0.55)";
DIA.widgetBorde   = hexToRgba(DIA.primary, 0.12);
DIA.widgetLinea   = hexToRgba(DIA.primary, 0.10);
DIA.etiquetaVelo  = hexToRgba(DIA.bg, 0.88);
DIA.etiquetaBorde = hexToRgba(DIA.border, 0.90);
DIA.jornadaIni    = hexToRgba(DIA.rampa1, 0.30);
DIA.jornadaFin    = hexToRgba(DIA.rampa2, 0.55);
DIA.jornadaMuesca = "rgba(255,255,255,0.75)";
// ── Carpetas (portadas de módulo, 2026-09-13) ────────────────────────────────
//  La fila seleccionada de una lista sobre el velo: tinte de marca, no el
//  tableSelected opaco (taparía el paisaje). Ver DESIGN.md, "Carpetas".
DIA.filaSeleccion = hexToRgba(DIA.primary, 0.08);
// ── Panel lateral en calma (2026-09-14) ──────────────────────────────────────
//  La "columna hundida" (dirección B del lienzo "Panel lateral en calma", decisión
//  del dueño): el panel del shell es una columna surfaceSunken con filo `border`,
//  la fila activa una pastilla `surface` con E.e1 y texto `primary`, y este
//  tinte de marca al pasar el cursor. Los `sidebar*` (navy) siguen para el
//  login, el portal del cliente y el panel de descargas.
DIA.panelFondo    = DIA.surfaceSunken;
DIA.panelHover    = hexToRgba(DIA.primary, 0.06);
DIA.panelActivo   = DIA.surface;                  // la pastilla de la fila activa del panel
DIA.panelTexto    = DIA.textMed;                  // las filas del panel (de noche, en blanco)
DIA.panelIcono    = DIA.textSub;                  // sus iconos (de noche, en blanco como sus textos)
DIA.panelFilo     = DIA.border;                   // el filo del panel suelto (2026-10-01); de noche, la línea tenue del marco
// ── Roles que de día coinciden y de noche se separan (2026-09-17, "Oscuro en calma") ──
//  El mismo color era texto y relleno: de noche el que se LEE se aclara y el que
//  RELLENA (con texto blanco encima) se queda en un tono medio. De día valen lo mismo.
DIA.primaryFill     = DIA.primary;
DIA.primaryDeepFill = DIA.primaryDeep;
DIA.accentFill      = DIA.accent;
DIA.successFill     = DIA.success;
DIA.criticalFill    = DIA.critical;
DIA.dangerFill      = DIA.danger;
DIA.warningFill     = DIA.warning;
DIA.markFill        = DIA.mark;
DIA.flotante        = DIA.surface;                  // menús, diálogos, lo que se abre encima
DIA.cristal         = "rgba(255,255,255,0.80)";     // lo que se pega arriba o abajo (GLASS.chrome); la barra superior del teléfono
DIA.pastilla        = hexToRgba(DIA.primary, 0.06);  // la miga y los botones de la barra superior del teléfono (GLASS.pastilla): relleno plano
DIA.paisajeTrazo    = DIA.primary;                  // las curvas del paisaje
// La barra superior en teclas (2026-10-08, lienzo "La barra superior", A elegida: "Implementa la
// recomendación"). En escritorio el cascarón (la barra superior y el pie) toma el tono del panel
// (de día el hundido, como cristal; de noche ya lo tenía) y sus piezas son TECLAS: el blanco de la
// fila activa del panel, más claras que la barra, con un filo y la sombra de contacto. Antes eran el
// acero al 6 % sobre el blanco de la barra: más oscuras que ella, se leían como pozos.
DIA.cascaron        = hexToRgba(DIA.surfaceSunken, 0.90);
DIA.tecla           = DIA.panelActivo;
DIA.teclaFilo       = "rgba(20,32,50,0.09)";
// El marcatextos (2026-10-22, lienzo "Marca de lo que ya cumplió", D; el dueño: "Probemos con un tipo marcatextos",
// "Si implementa así"): la banda detrás de un texto que se señala, en el ámbar suave de la firma. No es alarma: el
// texto encima conserva su color. Su espejo en papel es PDF.RESALTE (design-pdf.js).
DIA.resalte         = DIA.markBorder;

// ══════════════════════════════════════════════
//  LA PALETA DE NOCHE (2026-09-17, lienzo "Oscuro en calma", elegida por el dueño)
//  Grafito; el acero rellena y su familia aclarada se lee; estados apagados; la
//  ESCALERA de grises, cada uno con un papel: NT el área de trabajo (el fondo, el
//  marco, la lista, la superficie) · NC el cascarón (panel, barra superior, pie) ·
//  N2 lo que flota (menús, diálogos) · N3 lo que se toca (el borde de un campo o
//  un botón) · ND lo hundido. Una sola línea (blanco al
//  6-7 %), tres textos y un solo azul translúcido para lo activo. Lo que es papel
//  (pdf*, doc*) y lo que no depende del tema (sidebar*, onPrimary*) no cambia.
//  El relleno de la primaria (#4579A9) y el paisaje encendido son la
//  recomendación del lienzo, aplicada sin elección expresa (DESIGN.md, Modo oscuro).
// ══════════════════════════════════════════════
// ── La noche de Uttensio (2026-10-02, lienzo "La noche de Uttensio", lectura A) ──
//  El dueño, comparando con Uttensio: "está mejor logrado en cuanto a los colores
//  que implementa y dónde, el workspace es negro y lo demás un poco más claro";
//  entre bajar solo el fondo o el fondo con la lista, eligió los dos juntos. Los
//  tonos son los medidos en su captura de Uttensio: el lienzo #1B1B1D y el
//  cascarón #232326. Reemplaza la lógica del Finder (2026-09-18: el panel #1A1A1B
//  más oscuro que todo, el cascarón y el fondo #202021, lo que se lee #222223).
const NT = "#1B1B1D", NC = "#232326", N2 = "#2E2E30", N3 = "#3C3C3F";
const ND = "#161617";   // lo hundido (campos rellenos, pozos)
//  Los fondos de estado (*Light) suben 8 niveles, lo justo para que un estado se
//  lea sobre el gris único con 4.5:1 o más; sus filos (*Border) suben 19, lo mismo
//  que la superficie.
const NOCHE = {
  ...DIA,
  primary: "#5B9BD0", primaryDark: "#3B6B98", primaryDeep: "#CFDDEC", primaryLight: "rgba(91,155,208,0.16)", primaryMid: "#2D445E",
  accent: "#7BA9B2", accentHover: "#8FBAC2", accentLight: "#1E282A", accentBorder: "#415154",
  mark: "#C6A164", markDeep: "#CCA56A", markLight: "#2C261C", markBorder: "#514938",
  danger: "#D08A80", dangerLight: "#2E2220", dangerBorder: "#55413C",
  critical: "#CD8177", criticalLight: "#2A201E", criticalBorder: "#55413C", criticalDark: "#943B32",
  warning: "#C6A164", warningLight: "#28241C", warningBorder: "#514938",
  success: "#7FAE94", successLight: "#1F2722", successBorder: "#435249",
  purple: "#A7A1D0", purpleLight: "#262434", purpleBorder: "#47455C",
  bg: NT, surface: NT, surfaceSunken: ND, border: N3, borderLight: "rgba(255,255,255,0.07)", borderMid: "#49494D",
  text: "#E8E8EB", textMed: "#B4B4BB", textSub: "#B4B4BB", textMuted: "#A3A3AB", textDisabled: "#6E6E76",
  // La cebra y el hover bajaron con la lista (2026-10-02): guardan sobre NT los mismos pasos que tenían sobre #222223.
  tableHeader: NT, tableAlt: "#1F1F22", tableHover: "#242428", tableSelected: "rgba(91,155,208,0.16)",
  successVivid: "#7FC4A0", successVividLight: "#1B2B23",
  scrim: "rgba(0,0,0,0.60)", scrimMenu: "rgba(0,0,0,0.40)",
  dataViz2: "#CCA56A", dataViz4: "#D08A80",
  errorField: "#2A201E", errorFieldBorder: "#CD8177",
  gridLine: "rgba(255,255,255,0.07)", refLine: "rgba(198,161,100,0.45)",
  shine: "rgba(255,255,255,0.08)", skelSheen: "rgba(255,255,255,0.07)",
  rampa1: "#3B5673", rampa2: "#31668F",
  rampa1Light: "#202832", rampa1Deep: "#9FB6CE", rampa2Light: "#1B2734", rampa2Deep: "#86AAD0",
  widgetVelo: "transparent", widgetBorde: "rgba(255,255,255,0.08)", widgetLinea: "rgba(255,255,255,0.06)",
  etiquetaVelo: "rgba(46,46,48,0.92)", etiquetaBorde: "rgba(255,255,255,0.10)",
  jornadaIni: "rgba(63,89,120,0.55)", jornadaFin: "rgba(81,131,186,0.85)", jornadaMuesca: "rgba(255,255,255,0.18)",
  // El panel sube al cascarón (NC), un escalón sobre el área de trabajo, con la barra
  // superior y el pie (2026-10-02, "La noche de Uttensio"); su fila activa sube otro escalón.
  // Sus textos e iconos siguen en blanco ("los textos del panel en blanco", 2026-09-18).
  filaSeleccion: "rgba(91,155,208,0.14)", panelFondo: NC, panelHover: "rgba(255,255,255,0.05)", panelActivo: "#303033", panelTexto: "#E8E8EB", panelIcono: "#E8E8EB", panelFilo: "rgba(255,255,255,0.08)",
  primaryFill: "#4579A9", primaryDeepFill: "#2C4A6B", accentFill: "#2E7480", successFill: "#3F7A5C",
  criticalFill: "#A8453B", dangerFill: "#94583F", warningFill: "#86621F", markFill: "#8F6A1E",
  // El cristal de la barra superior y el pie: sobre el área de trabajo (NT) se lee NC.
  flotante: N2, cristal: "rgba(36,36,39,0.88)", pastilla: "rgba(255,255,255,0.06)", paisajeTrazo: "#C8C8D2",
  // Los espejos del PDF que se usan FUERA de una hoja (renglones de sección del
  // editor, filas apagadas del despacho) toman la escalera; dentro de una hoja
  // ([data-gb-papel]) vuelven a los del PDF.
  pdfStripe: N2, pdfAlt: "#252527", pdfBorder: N3, navyTinta: "#E8E8EB",
};
// Alias de noche (los mismos que de día, sobre los valores de noche)
Object.assign(NOCHE, {
  axisLabel: NOCHE.textMuted,
  overdue: NOCHE.danger, overdueLight: NOCHE.dangerLight, overdueBorder: NOCHE.dangerBorder,
  dataViz1: NOCHE.primary, dataViz3: NOCHE.accent, dataViz5: NOCHE.success, dataViz6: NOCHE.purple,
  headerText: NOCHE.primary, focus: NOCHE.primary,
  resalte: NOCHE.markBorder,
  // La barra superior en teclas: de noche el cascarón es el cristal de siempre y la tecla, la fila activa del panel.
  cascaron: NOCHE.cristal, tecla: NOCHE.panelActivo, teclaFilo: NOCHE.borderLight,
});

// ══════════════════════════════════════════════
//  LA PALETA DEL TELÉFONO (2026-10-12, lienzo "GEOBAJA en el iPhone", dirección A
//  elegida por el dueño: "Implementa A"). De día y solo en el teléfono
//  (<html data-aparato="telefono">, 767 px o menos; lo pone public/tema.js antes
//  de React y useApariencia mientras vive el cascarón): la geometría de iOS con la
//  identidad de GEOBAJA. El acero, el teal, el ámbar, las etapas y el papel no
//  cambian; cambian el fondo (el agrupado de iOS, enfriado a la tinta), lo
//  hundido (un relleno con transparencia, sin borde), las líneas (con
//  transparencia; 0.5 px en pantallas de 2x, --gb-hairline), el texto secundario
//  (la tinta con transparencia, 4.5:1 o más sobre blanco), el velo de la hoja
//  inferior y el cristal, que es vidrio (desenfoque 20, saturación 180 %). La
//  noche no entra al teléfono: sigue en claro (DESIGN.md, "El teléfono nativo").
// ══════════════════════════════════════════════
const TELEFONO = {
  ...DIA,
  bg:            "#F2F4F7",
  surfaceSunken: "rgba(118,126,140,0.12)",
  border:        "rgba(27,34,48,0.14)",
  borderLight:   "rgba(27,34,48,0.10)",
  borderMid:     "rgba(27,34,48,0.22)",
  textSub:       "rgba(27,34,48,0.68)",   // 5.5:1 sobre blanco
  textMuted:     "rgba(27,34,48,0.62)",   // 4.6:1 sobre blanco
  textDisabled:  "rgba(27,34,48,0.36)",   // marcadores de posición y chevrones: nunca información
  primaryLight:  "rgba(35,71,111,0.12)",  // el botón teñido y la fila elegida
  tableHeader:   "rgba(118,126,140,0.08)",
  tableAlt:      "rgba(118,126,140,0.06)",
  tableHover:    "rgba(118,126,140,0.20)", // lo que se toca, mientras se toca
  scrim:         "rgba(16,25,40,0.40)",    // detrás de la hoja inferior
  cristal:       "rgba(248,249,251,0.78)", // el vidrio de las barras
  panelFondo:    "#F2F4F7",                // el cajón, sobre el mismo fondo agrupado
};
Object.assign(TELEFONO, {
  axisLabel: TELEFONO.textMuted,
  panelFilo: TELEFONO.border,
  etiquetaVelo: "rgba(242,244,247,0.88)",
  etiquetaBorde: TELEFONO.border,
});

// ══════════════════════════════════════════════
//  LA PIEL DE CAMPO (2026-10-27). La app de campo de compactaciones (la tablet del
//  laboratorista, src/modules/compactacion/campo/) lleva la piel de la ronda 8 del
//  lienzo "Compactaciones en campo", que el dueño eligió por sobria ("deja el diseño
//  de la contrapropuesta en cuanto a geometría pero los estilos de la ronda 8, que
//  parecen los más sobrios"): Uttensio traducida al dedo, con vidrio, píldoras y el
//  azul de acción de iOS. Es OTRA piel, no la del escritorio: vive en sus propias
//  variables (--gbc-*) dentro de [data-gb-campo], de día y de noche (sigue el
//  data-tema de la cuenta), y ningún módulo de escritorio la usa. Los hexes viven
//  aquí, como todos (ESLint).
// ══════════════════════════════════════════════
const CAMPO_DIA = {
  fondo: "#F6F6F8", escritorio: "#E8E8EB", papel: "#FFFFFF", alzado: "#FFFFFF",
  vidrio: "rgba(246,246,249,0.72)", vidrio2: "rgba(255,255,255,0.84)", vidrioLuz: "rgba(255,255,255,0.80)", vidrioLinea: "rgba(0,0,0,0.09)",
  panel: "rgba(243,243,246,0.90)", relleno: "rgba(118,118,128,0.12)", relleno2: "rgba(118,118,128,0.20)", seleccion: "rgba(0,0,0,0.065)",
  sep: "rgba(0,0,0,0.09)", sep2: "rgba(0,0,0,0.16)",
  tinta: "#1D1D1F", tinta2: "#5C5C61", tinta3: "#A1A1A6",
  accion: "#007AFF", accionTexto: "#0062CC", accionFill: "#0071E3", accionSuave: "rgba(0,122,255,0.11)",
  verde: "#34C759", rojo: "#FF3B30", rojoTexto: "#D70015",
  real: "#0071A4", realSuave: "rgba(0,113,164,0.12)", virt: "#8944AB", virtSuave: "rgba(137,68,171,0.11)", defl: "#C93400", deflSuave: "rgba(201,52,0,0.10)",
  viga: "#C7C7CC", vigaBorde: "#8E8E93", velo: "rgba(0,0,0,0.20)",
  flota: "0 10px 30px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
  sombraHoja: "0 16px 48px rgba(0,0,0,0.18)", sombraTecla: "0 1px 0 rgba(0,0,0,0.16)",
  perilla: "0 1px 3px rgba(0,0,0,0.16)", boton: "0 1px 3px rgba(0,0,0,0.14)",
  lista: "0 0 0 0.5px rgba(0,0,0,0.09), 0 1px 2px rgba(0,0,0,0.04)",
};
const CAMPO_NOCHE = {
  ...CAMPO_DIA,
  fondo: "#161618", escritorio: "#1B1B1D", papel: "#232326", alzado: "#3A3A3E",
  vidrio: "rgba(40,40,43,0.72)", vidrio2: "rgba(58,58,62,0.82)", vidrioLuz: "rgba(255,255,255,0.14)", vidrioLinea: "rgba(255,255,255,0.10)",
  panel: "rgba(38,38,41,0.90)", relleno: "rgba(118,118,128,0.24)", relleno2: "rgba(118,118,128,0.34)", seleccion: "rgba(255,255,255,0.09)",
  sep: "rgba(255,255,255,0.09)", sep2: "rgba(255,255,255,0.16)",
  tinta: "#F2F2F7", tinta2: "#AEAEB4", tinta3: "#6C6C72",
  accion: "#0A84FF", accionTexto: "#4DA3FF", accionFill: "#0A6EE0", accionSuave: "rgba(10,132,255,0.16)",
  verde: "#30D158", rojo: "#FF453A", rojoTexto: "#FF6961",
  real: "#64D2FF", realSuave: "rgba(100,210,255,0.15)", virt: "#DA8FFF", virtSuave: "rgba(218,143,255,0.15)", defl: "#FFB340", deflSuave: "rgba(255,179,64,0.14)",
  viga: "#636366", vigaBorde: "#8E8E93", velo: "rgba(0,0,0,0.48)",
  flota: "0 10px 30px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.30)",
  sombraHoja: "0 16px 48px rgba(0,0,0,0.55)", sombraTecla: "0 1px 0 rgba(0,0,0,0.40)",
  lista: "0 0 0 0.5px rgba(255,255,255,0.09), 0 1px 2px rgba(0,0,0,0.04)",
};
// La letra de campo es la del aparato (en Android, Roboto; decisión del dueño en la ronda 6, con Inter de respaldo).
const CAMPO_FAMILIAS = {
  ui: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, Roboto, 'Inter', 'Helvetica Neue', sans-serif",
  disp: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro', system-ui, Roboto, 'Inter', 'Helvetica Neue', sans-serif",
  mono: "'SF Mono', ui-monospace, Menlo, 'Roboto Mono', 'Geist Mono', Consolas, monospace",
};
// Los compuestos (sombras de vidrio y de hoja) se arman con las variables para que sigan al tema.
const CAMPO_COMPUESTOS = {
  vidrioFlota: "inset 0 1px 0 var(--gbc-vidrioLuz), 0 0 0 0.5px var(--gbc-vidrioLinea), var(--gbc-flota)",
  vidrioBarra: "inset 0 1px 0 var(--gbc-vidrioLuz), 0 0 0 0.5px var(--gbc-vidrioLinea), 0 1px 3px rgba(0,0,0,0.06)",
  hoja: "inset 0 1px 0 var(--gbc-vidrioLuz), 0 0 0 0.5px var(--gbc-vidrioLinea), var(--gbc-sombraHoja)",
  desenfoque: "blur(24px) saturate(1.8)",
};
const varsCampo = (p) => Object.keys(p).map((k) => `--gbc-${k}:${p[k]};`).join("")
  + Object.keys(CAMPO_FAMILIAS).map((k) => `--gbc-f-${k}:${CAMPO_FAMILIAS[k]};`).join("")
  + Object.keys(CAMPO_COMPUESTOS).map((k) => `--gbc-${k}:${CAMPO_COMPUESTOS[k]};`).join("");
const CAMPO = {};
for (const k of Object.keys(CAMPO_DIA)) CAMPO[k] = `var(--gbc-${k})`;
for (const k of Object.keys(CAMPO_COMPUESTOS)) CAMPO[k] = `var(--gbc-${k})`;
CAMPO.ui = "var(--gbc-f-ui)"; CAMPO.disp = "var(--gbc-f-disp)"; CAMPO.mono = "var(--gbc-f-mono)";
// Lo que un canvas o un PNG de campo necesite en crudo.
const CAMPO_PALETAS = Object.freeze({ dia: Object.freeze({ ...CAMPO_DIA }), noche: Object.freeze({ ...CAMPO_NOCHE }) });

// C: lo que cambia de noche o en el teléfono es una variable CSS (el tema y el
// aparato se eligen en la raíz, <html data-tema="oscuro"> y
// <html data-aparato="telefono">, sin volver a dibujar nada); lo que no cambia se
// queda como valor fijo, así los PDF, los lienzos de canvas y el QR, que
// necesitan un color de verdad, siguen recibiendo el mismo hex.
const TEMA = Object.keys(DIA).filter((k) => DIA[k] !== NOCHE[k] || DIA[k] !== TELEFONO[k]);
const C = {};
for (const k of Object.keys(DIA)) C[k] = TEMA.includes(k) ? `var(--gb-${k})` : DIA[k];
// Los valores de día en crudo, para lo que es PAPEL o necesita un hex real
// (canvas, QR, un PNG): nunca se oscurece.
const C_DIA = Object.freeze({ ...DIA });

// ══════════════════════════════════════════════
//  F — FAMILIAS TIPOGRÁFICAS Y NUMERALES
//  Geist, Inter y Geist Mono se cargan en index.html (link + preconnect).
//  F.num va en TODO dato numérico (montos, cantidades, folios): los dígitos
//  ocupan el mismo ancho y las cifras alinean por columna.
//  Las familias son VARIABLES (2026-10-12, dirección A de "GEOBAJA en el
//  iPhone"): de día y en escritorio, Inter, Geist y Geist Mono; en el teléfono,
//  la letra del aparato (SF Pro en el iPhone, Roboto en Android). Un canvas no
//  entiende var(): ahí va F.crudo (los nombres del día, como C_DIA).
// ══════════════════════════════════════════════
const FAMILIAS = {
  dia: {
    ui:      "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    display: "'Geist', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    mono:    "'Geist Mono', ui-monospace, 'Cascadia Code', Consolas, monospace",
  },
  telefono: {
    ui:      "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, 'Segoe UI', Roboto, sans-serif",
    display: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, 'Segoe UI', Roboto, sans-serif",
    mono:    "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
  },
};
const F = {
  ui:      "var(--gb-f-ui)",
  display: "var(--gb-f-display)",
  mono:    "var(--gb-f-mono)",
  num:     { fontVariantNumeric: "tabular-nums", fontFeatureSettings: "'tnum' 1" },
  crudo:   FAMILIAS.dia,
};

// ══════════════════════════════════════════════
//  T — ESCALA TIPOGRÁFICA (única; nada de 9.5/10.5/12.5)
//  Uso: style={{ ...T.body }} · combinable: {{ ...T.caption, color: C.textSub }}
//  Los tamaños son VARIABLES (2026-10-12, dirección A): T_PX guarda los números
//  de siempre (:root, escritorio y las vistas clásicas del teléfono) y T_NATIVO
//  la escala del teléfono nativo (la de iOS: 17 de cuerpo, 15 y 13), que solo
//  entra dentro de [data-gb-nativo] en el teléfono: el cascarón del teléfono hoy
//  y las pantallas del bolsillo cuando se construyan. Nadie hace cuentas con
//  T.x.fontSize (0 usos el 2026-10-12); quien las necesite usa T_PX.
// ══════════════════════════════════════════════
const T_PX = {
  caption:   { fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", lineHeight: 1.3 },
  micro:     { fontSize: 11, fontWeight: 500, lineHeight: 1.35 },
  bodySm:    { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
  body:      { fontSize: 13, fontWeight: 400, lineHeight: 1.45 },
  strong:    { fontSize: 14, fontWeight: 600, lineHeight: 1.35 },
  headingSm: { fontSize: 16, fontWeight: 700, letterSpacing: -0.2, lineHeight: 1.25 },
  headingMd: { fontSize: 18, fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.25 },
  headingLg: { fontSize: 22, fontWeight: 700, letterSpacing: -0.4, lineHeight: 1.2 },
  display:   { fontSize: 24, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.2 },
};
// La escala nativa, por rol de T: el Rótulo en calma del teléfono es 13/600/0.05em.
const T_NATIVO = {
  caption:   { fontSize: 13, fontWeight: 600, letterSpacing: 0.65, lineHeight: "18px" },
  micro:     { fontSize: 13, lineHeight: "18px" },
  bodySm:    { fontSize: 15, lineHeight: "20px" },
  body:      { fontSize: 17, lineHeight: "22px" },
  strong:    { fontSize: 17, lineHeight: "22px" },
  headingSm: { fontSize: 20, lineHeight: "25px" },
  headingMd: { fontSize: 22, lineHeight: "28px" },
  headingLg: { fontSize: 28, lineHeight: "34px" },
  display:   { fontSize: 34, lineHeight: "41px" },
};
const T = {};
for (const [k, t] of Object.entries(T_PX)) {
  T[k] = {
    ...t,
    fontSize: `var(--gb-fs-${k})`,
    lineHeight: `var(--gb-lh-${k})`,
    ...(k === "caption" ? { fontWeight: "var(--gb-fw-caption)", letterSpacing: "var(--gb-ls-caption)" } : {}),
  };
}
// px() solo para lo que React escribe en px cuando es un número (el tamaño y el espaciado). El
// interlineado de T_PX es un MÚLTIPLO de la letra (1.3 = 130 %) y va sin unidad: con px() quedaba
// en "1.3px", una línea de poco más de 1 px, y todo texto con ...T.x que recorta (la elipsis)
// enseñaba solo una rebanada de sus letras (2026-10-13, la pista de la búsqueda en Presupuestos).
// El de T_NATIVO ya llega en px ("18px").
const px = (v) => (typeof v === "number" ? `${v}px` : v);
const varsEscala = (escala) => Object.entries(escala).map(([k, t]) => [
  t.fontSize != null ? `--gb-fs-${k}:${px(t.fontSize)};` : "",
  t.lineHeight != null ? `--gb-lh-${k}:${t.lineHeight};` : "",
  k === "caption" && t.fontWeight != null ? `--gb-fw-caption:${t.fontWeight};` : "",
  k === "caption" && t.letterSpacing != null ? `--gb-ls-caption:${px(t.letterSpacing)};` : "",
].join("")).join("");
const varsFamilias = (f) => `--gb-f-ui:${f.ui};--gb-f-display:${f.display};--gb-f-mono:${f.mono};`;

// ══════════════════════════════════════════════
//  SP — ESPACIADO · escala 4/8 (padding, margin, gap siempre múltiplos de 4)
// ══════════════════════════════════════════════
const SP = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 32 };

// ══════════════════════════════════════════════
//  R — RADIOS · un solo lenguaje
//  badges/chips 4-6 · controles (inputs/botones) 6 · menús/popovers 10 ·
//  tarjetas 12 · modales 14 · pill 999 · círculo "50%"
// ══════════════════════════════════════════════
const R = {
  xs:  4,
  sm:  6,
  md:  8,
  lg:  10,
  xl:  12,
  xxl: 14,
  // roles
  ctl:   6,      // inputs, botones, selects
  menu:  10,     // dropdowns, popovers
  card:  12,     // tarjetas
  modal: 14,     // modales y drawers
  bubble: 18,    // burbujas de chat (geometría redonda estilo iMessage)
  marco:  18,    // el marco en calma: la ventana y la barra de una carpeta (2026-09-17)
  hoja:   20,    // la hoja inferior del teléfono, solo arriba (2026-10-12, dirección A)
  pill:  999,
  round: "50%",
};

// ══════════════════════════════════════════════
//  E — ELEVACIÓN · sombra tintada rgba(20,32,50,…), nunca negro puro.
//  e1 tarjeta en reposo · e2 flotante ligero (hover, dropdown) ·
//  e3 modal/drawer · e4 capa máxima (previews de página completa) ·
//  menu: el menú desplegado (2026-09-18).
//  focus/glow*: el lenguaje de luz de la marca (extensión del LogoLoader).
// ══════════════════════════════════════════════
const E_DIA = {
  e0: "none",
  e1: "0 1px 2px rgba(20,32,50,0.05), 0 1px 1px rgba(20,32,50,0.03)",
  e2: "0 2px 8px rgba(20,32,50,0.07), 0 1px 3px rgba(20,32,50,0.05)",
  e3: "0 12px 32px rgba(20,32,50,0.14), 0 2px 8px rgba(20,32,50,0.07)",
  e4: "0 24px 64px rgba(20,32,50,0.22), 0 4px 16px rgba(20,32,50,0.10)",
  // menu (2026-09-18, dirección B del lienzo "Menús con profundidad", elegida por
  //  el dueño): el menú desplegado ya no baja la app con un velo, despega de ella
  //  con sombra EN CAPAS: filo de 1 px, contacto, una media y una ambiental con
  //  dispersión negativa para que la sombra caiga DEBAJO del menú y no a los
  //  lados. Se lee con canto a cualquier tamaño (la miga, la fila, la burbuja).
  menu: "0 0 0 1px rgba(20,32,50,0.05), 0 1px 2px rgba(20,32,50,0.10), 0 8px 16px -4px rgba(20,32,50,0.16), 0 24px 48px -8px rgba(20,32,50,0.26)",
  // Luz de marca
  focus:       `0 0 0 2px ${DIA.onPrimary}, 0 0 0 3px ${DIA.focus}, 0 0 12px ${hexToRgba(DIA.focus, 0.30)}`,
  glowPrimary: "0 1px 2px rgba(20,32,50,0.10), 0 0 14px rgba(91,155,208,0.30)",
  glowAccent:  "0 1px 2px rgba(20,32,50,0.10), 0 0 14px rgba(14,124,139,0.25)",
  // glowSoft (v4): resplandor de estado activo en nav. Derivado del glow azul de
  //  `glowPrimary` (sidebarAccent @28%), sin la capa de elevación — para halo de
  //  ítem activo, no para tarjetas. Vive en E con las demás luces de marca.
  glowSoft:    "0 0 10px rgba(91,155,208,0.28)",
  // focusInput (Obj. A): glow suave de campo de texto (C.focus @20%). El campo ya
  //  lleva outline 2px C.focus del navegador; este es solo el resplandor, sin el
  //  anillo blanco+color completo de E.focus (reservado a botones vía .gb-focus).
  focusInput:  `0 0 10px ${hexToRgba(DIA.focus, 0.20)}`,
  // press: confirmación sensorial del click (gb-press). Anillo en azul de marca
  //  (primary, el mismo del foco desde 2026-09-09: se distinguen por gesto, el
  //  press entra en 0ms al clic y el foco solo aparece con teclado) que entra en
  //  0ms (snap de conmutador) y se desvanece con la transición normal al soltar.
  //  Luz, no profundidad: el press no mueve geometría.
  press:       `0 0 0 2px ${hexToRgba(DIA.primary, 0.32)}, 0 0 8px ${hexToRgba(DIA.primary, 0.16)}`,
  scrollEdge:  "rgba(20,32,50,0.10)",   // (Obj. C) tinte de la sombra de borde de scroll horizontal
};
// De noche la sombra casi no se ve: lo que despega es la luz del borde de arriba.
const E_NOCHE = {
  ...E_DIA,
  e1: "0 1px 2px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
  e2: "0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
  e3: "0 16px 40px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07)",
  e4: "0 24px 64px rgba(0,0,0,0.65), 0 4px 16px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.08)",
  menu: "0 0 0 1px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.50), 0 8px 18px -4px rgba(0,0,0,0.55), 0 28px 56px -8px rgba(0,0,0,0.70), inset 0 1px 0 rgba(255,255,255,0.08)",
  focus:       `0 0 0 2px ${NOCHE.bg}, 0 0 0 3px ${NOCHE.focus}, 0 0 12px ${hexToRgba(NOCHE.focus, 0.30)}`,
  glowPrimary: "0 1px 2px rgba(0,0,0,0.40), 0 0 14px rgba(91,155,208,0.30)",
  glowAccent:  "0 1px 2px rgba(0,0,0,0.40), 0 0 14px rgba(123,169,178,0.25)",
  focusInput:  `0 0 10px ${hexToRgba(NOCHE.focus, 0.25)}`,
  press:       `0 0 0 2px ${hexToRgba(NOCHE.primary, 0.35)}, 0 0 8px ${hexToRgba(NOCHE.primary, 0.18)}`,
  scrollEdge:  "rgba(0,0,0,0.45)",
};
const E_TEMA = Object.keys(E_DIA).filter((k) => E_DIA[k] !== E_NOCHE[k]);
const E = {};
for (const k of Object.keys(E_DIA)) E[k] = E_TEMA.includes(k) ? `var(--gb-e-${k})` : E_DIA[k];

// ══════════════════════════════════════════════
//  M — MOTION · confirmación, no decoración.
//  fast 120ms (hover/press) · base 160ms (lo demás) · slow 240ms (drawers,
//  entradas de capa). Easing de marca M.ease; M.easeOut para entradas.
// ══════════════════════════════════════════════
const M = {
  fast: "120ms",
  base: "160ms",
  slow: "240ms",
  stagger: "45ms",   // (Obj. F) paso de la entrada escalonada gb-stagger
  ease:    "cubic-bezier(.2,.7,.2,1)",
  easeOut: "cubic-bezier(.16,1,.3,1)",
  // presets listos para transition:
  colors:    "background-color 160ms cubic-bezier(.2,.7,.2,1), border-color 160ms cubic-bezier(.2,.7,.2,1), color 160ms cubic-bezier(.2,.7,.2,1), box-shadow 160ms cubic-bezier(.2,.7,.2,1)",
  transform: "transform 120ms cubic-bezier(.2,.7,.2,1)",
  all:       "all 160ms cubic-bezier(.2,.7,.2,1)",
};

// ══════════════════════════════════════════════
//  Z — CAPAS · escala única de z-index (espejo del stacking real de la app)
// ══════════════════════════════════════════════
const Z = {
  tableHeader: 5,   // (Obj. C) th sticky: sobre las celdas, muy por debajo del header del shell
  toolbar:    10,   // barra de comandos sticky de un workspace: sobre el documento
                    //   y sobre el th sticky, muy por debajo del chrome del shell

  scrim:     199,   // overlay del drawer móvil
  nav:       200,   // sidebar
  catcher:   499,   // click-away invisible de dropdowns
  header:    600,   // header sticky del shell
  aviso:     620,   // el aviso que flota con los procesos largos (2026-09-22): sobre el
                    //   contenido y el header, bajo menús, diálogos y toasts
  menu:      650,   // dropdowns del shell
  bottomNav: 700,   // bottom nav móvil
  // Capa de un MENÚ DESPLEGADO con su velo (ver MenuScrim). Va sobre todo el
  // chrome del shell —el velo tiene que poder atenuar header y bottom nav— y
  // por debajo de modales y toasts: un menú abierto no puede tapar ni una
  // confirmación ni un aviso. Sin este token cada sitio inventaba un número.
  menuLayer: 900,
  modal:     1000,  // modales (base; anidados suman)
  help:      9000,  // ayuda de atajos
  toast:     9999,  // toasts
  popover:   10000, // popover del Select (portal)
};

// ══════════════════════════════════════════════
//  GLASS — TRANSLUCIDEZ · criterio estricto:
//  SOLO capas transitorias que flotan sobre contenido y donde conservar el
//  contexto detrás tiene función: chrome sticky (header/bottom-nav/footer
//  sobre tablas largas) y scrims de modal/drawer. Las superficies donde vive
//  la información (tarjetas, tablas, paneles de modal, menús) son OPACAS.
//  Solo dos intensidades de blur: 8px chrome, 3px scrim.
// ══════════════════════════════════════════════
// 0.80 + blur 10: translucidez PERCEPTIBLE cuando pasa contenido debajo,
// sin comprometer legibilidad (a 0.94 era indistinguible de un opaco).
// Vidrio NEUTRO, sin pigmento propio: el color lo pone el fondo. El tinte
// crema anterior se leía como banda ámbar sobre contenido blanco.
// El desenfoque es una variable: 10 en escritorio y, en el teléfono, el vidrio de
// iOS (20 con saturación 180 %), como el fondo (C.cristal).
const CRISTAL = {
  background: "rgba(255,255,255,0.80)",
  backdropFilter: "var(--gb-cristal-filtro)",
  WebkitBackdropFilter: "var(--gb-cristal-filtro)",
};
const GLASS = {
  chrome: { ...CRISTAL, background: C.cristal },
  // LO QUE FLOTA (2026-09-17, el dueño: "aplica diseño a la barra inferior
  // también para que no se pierda en la pantalla, los mismos que a la barra de
  // acciones"): la burbuja de acciones y las barras de abajo (la de un nivel y
  // la de estado de una carpeta) llevan UNA sola piel: el cristal, su filo y
  // la sombra que la despega. Es el estilo "Cristal", el de arranque; cuando
  // exista la Apariencia por aparato (Cristal, Vidrio, Sólido), se cambia aquí.
  // Personalizable por aparato (Apariencia, en la cuenta): Cristal (el de
  // arranque), Vidrio o Sólido; <html data-flota> elige, de día y de noche.
  flota: {
    background: "var(--gb-flota-fondo)",
    backdropFilter: "var(--gb-flota-filtro)",
    WebkitBackdropFilter: "var(--gb-flota-filtro)",
    border: "var(--gb-flota-borde)",
    boxShadow: "var(--gb-flota-sombra)",
  },
  // La miga y los botones de la barra superior (2026-09-18, el dueño: "miga al
  // color de la barra con el estilo y botones de la barra superior en estilo
  // calma"). PLANOS desde el 2026-10-02 (el dueño, de noche: "la miga y en general
  // la barra superior tiene un diseño que parece muy resaltado y profundo,
  // podríamos alisar la dimensión de estos elementos"); del todo lisa "dista del
  // panel y de la burbuja, encontremos el punto medio": un relleno un escalón
  // sobre la barra, el filo de la burbuja (el de su piel, casi el del panel) y la
  // sombra de contacto E.e1 en vez de la e2 de antes. Desde el 2026-10-08 es solo
  // del teléfono: en escritorio la barra superior va en teclas (abajo).
  pastilla: {
    background: C.pastilla,
    border: "var(--gb-flota-borde)",
    boxShadow: E.e1,
  },
  // La barra superior en teclas (2026-10-08, lienzo "La barra superior", A): el
  // cascarón de escritorio (la barra superior y el pie) en el tono del panel, y
  // sus piezas (el botón del panel, la miga, el grupo de Buscar, Buzón, Mensajes
  // y Avisos, la cuenta) como teclas: la fila activa del panel, más clara que la
  // barra. Ni resaltada (la sombra de contacto de siempre) ni lisa.
  cascaron: { ...CRISTAL, background: C.cascaron },
  tecla: {
    background: C.tecla,
    border: `1px solid ${C.teclaFilo}`,
    boxShadow: E.e1,
  },
  scrim: {
    background: C.scrim,
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
  },
};

// ══════════════════════════════════════════════════════════════════
//  HOJA BASE `gb-base` — estados de componente definidos UNA vez.
//  Clases: gb-i (transición curada) · gb-press (pressable) · gb-row (fila
//  navegable; su gb-asoma aparece al señalarla) · gb-ghost (se tiñe al hover) · gb-lift (tarjeta que se eleva) · gb-tarjeta (tarjeta que entra sin elevarse: al pulsarla baja, sin anillo) ·
//  gb-entity (enlace a ficha: color en reposo, pozo al hover) · gb-tint /
//  gb-tint-critical (tinte de marca / destructivo al hover) · gb-dim (relleno
//  suave que se oscurece) · gb-handle (asa de redimension) · gb-asa (asa en el
//  aire entre dos recuadros) · gb-tecla-fila (fila dentro de una tecla) ·
//  gb-asa-columna (el borde de una columna de la lista, o de la vista previa, que se arrastra) ·
//  gb-focus (ring de teclado con resplandor) · gb-shine (barrido de luz en
//  hover de acción primaria) · gb-skel (skeleton que respira como el loader) ·
//  gb-resolve (flash de fila resuelta) · .num-tabular/td.num/th.num (cifras).
//  Utilidades responsivas r-* (breakpoint único 767px, igual que useIsMobile).
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
//  EL TEMA (2026-09-17): las variables de día en :root, las de noche con
//  <html data-tema="oscuro">. La Apariencia (components/Apariencia.js) pone el
//  atributo; sin él, todo es de día (el login, el portal y las páginas públicas).
//  · Lo que flota (burbuja y barras): <html data-flota="vidrio|solido">; sin él, Cristal.
//  · El PAPEL es una isla: dentro de [data-gb-papel] vuelven los valores de día,
//    así una hoja (carátula, presupuesto, oficio, hoja viva) sigue blanca y sus
//    campos se leen como de día.
//  · Un NIVEL vive en el mismo marco que la carpeta, de noche y desde el
//    2026-09-18 también de día: [data-gb-nivel] (lo pone la burbuja en su nivel)
//    dibuja el marco con el tono de lo que se lee y el fondo solo en los
//    márgenes; un nivel de hoja ([data-gb-nivel-hoja]) no lleva marco. El marco
//    es el borde del nivel (2026-09-18, dueño: la burbuja "toca el límite y eso
//    se percibe como inexactitud en la geometría"): --gb-nivel-marco (12 px; 0 en
//    un nivel de hoja) se suma a toda
//    distancia medida desde el borde (burbuja, barra, relleno del lienzo), así
//    lo que flota guarda con el marco el mismo aire que de día guarda con el
//    cascarón; ::after pinta la línea del marco ENCIMA del contenido y tapa con
//    el fondo lo que se desplaza fuera de él.
// ══════════════════════════════════════════════════════════════════
const FLOTA = {
  dia: {
    cristal: { fondo: DIA.cristal, filtro: "blur(10px)", borde: `1px solid ${DIA.widgetBorde}`, sombra: E_DIA.e2 },
    vidrio:  { fondo: "linear-gradient(180deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.62) 100%)", filtro: "blur(28px) saturate(180%)", borde: "1px solid rgba(20,32,50,0.09)", sombra: "inset 0 1px 0 rgba(255,255,255,0.95), 0 18px 40px rgba(20,32,50,0.14), 0 2px 8px rgba(20,32,50,0.07)" },
    solido:  { fondo: DIA.surface, filtro: "none", borde: "1px solid rgba(20,32,50,0.10)", sombra: "0 12px 28px rgba(20,32,50,0.12), 0 2px 6px rgba(20,32,50,0.06)" },
  },
  noche: {
    // De noche el Cristal sale del tono de lo que flota (N2), un paso arriba de lo que tiene detrás
    // (N2 es #2E2E30, sobre el área de trabajo #1B1B1D y el cascarón #232326).
    cristal: { fondo: "rgba(46,46,48,0.88)", filtro: "blur(10px)", borde: "1px solid rgba(255,255,255,0.09)", sombra: "0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)" },
    vidrio:  { fondo: "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%), rgba(58,58,64,0.62)", filtro: "blur(28px) saturate(170%) brightness(0.62)", borde: "1px solid rgba(255,255,255,0.13)", sombra: "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.30), 0 12px 28px rgba(0,0,0,0.50), 0 2px 6px rgba(0,0,0,0.35)" },
    solido:  { fondo: N2, filtro: "none", borde: "1px solid rgba(255,255,255,0.10)", sombra: "inset 0 1px 0 rgba(255,255,255,0.08), 0 12px 28px rgba(0,0,0,0.50), 0 2px 6px rgba(0,0,0,0.35)" },
  },
  // El teléfono (2026-10-12, dirección A): lo que flota es vidrio con una línea fina y sin sombra;
  // el vidrio y su línea lo separan de lo que pasa debajo. No se elige en Apariencia.
  telefono: { fondo: TELEFONO.cristal, filtro: "blur(20px) saturate(180%)", borde: "var(--gb-hairline) solid rgba(27,34,48,0.22)", sombra: "none" },
};
const varsColor = (p) => TEMA.map((k) => `--gb-${k}:${p[k]};`).join("");
const varsSombra = (e) => E_TEMA.map((k) => `--gb-e-${k}:${e[k]};`).join("");
const varsFlota = (f) => `--gb-flota-fondo:${f.fondo};--gb-flota-filtro:${f.filtro};--gb-flota-borde:${f.borde};--gb-flota-sombra:${f.sombra};`;
// El teléfono (2026-10-12, "GEOBAJA en el iPhone", dirección A): <html data-aparato="telefono"> enciende
// la paleta del teléfono, el vidrio, la letra del aparato y la línea fina (0.5 px donde la pantalla es de
// 2x o más); la escala nativa de T entra solo dentro de [data-gb-nativo]. El papel vuelve a la letra del
// día como vuelve a sus colores: el documento se lee igual en todo aparato.
export const CSS_TEMA = `
:root{${varsColor(DIA)}${varsSombra(E_DIA)}${varsFlota(FLOTA.dia.cristal)}${varsFamilias(FAMILIAS.dia)}${varsEscala(T_PX)}--gb-cristal-filtro:blur(10px);--gb-hairline:1px;}
:root[data-flota="vidrio"]{${varsFlota(FLOTA.dia.vidrio)}}
:root[data-flota="solido"]{${varsFlota(FLOTA.dia.solido)}}
:root[data-tema="oscuro"]{color-scheme:dark;${varsColor(NOCHE)}${varsSombra(E_NOCHE)}${varsFlota(FLOTA.noche.cristal)}}
:root[data-tema="oscuro"][data-flota="vidrio"]{${varsFlota(FLOTA.noche.vidrio)}}
:root[data-tema="oscuro"][data-flota="solido"]{${varsFlota(FLOTA.noche.solido)}}
:root[data-aparato="telefono"]{${varsColor(TELEFONO)}${varsFlota(FLOTA.telefono)}${varsFamilias(FAMILIAS.telefono)}--gb-cristal-filtro:blur(20px) saturate(180%);}
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx){:root[data-aparato="telefono"]{--gb-hairline:0.5px;}}
:root[data-aparato="telefono"] [data-gb-nativo]{${varsEscala(T_NATIVO)}}
[data-gb-papel]{color-scheme:light;${varsColor(DIA)}${varsSombra(E_DIA)}${varsFamilias(FAMILIAS.dia)}}
/* La piel de campo (2026-10-27): sus variables viven solo dentro de la app de campo y siguen el tema de la cuenta. */
[data-gb-campo]{${varsCampo(CAMPO_DIA)}}
:root[data-tema="oscuro"] [data-gb-campo]{color-scheme:dark;${varsCampo(CAMPO_NOCHE)}}
[data-gb-campo] button,[data-gb-campo] input{font:inherit;color:inherit;-webkit-tap-highlight-color:transparent;}
[data-gb-campo] button:not(:disabled){cursor:pointer;}
[data-gb-campo] *::-webkit-scrollbar{width:0;height:0;}
[data-gb-campo] *{scrollbar-width:none;}
/* De noche el logo del panel va claro (2026-09-18, el dueño sobre el lienzo "Menús con profundidad": "en modo nocturno deja el logo en blanco como este mockup"): la silueta del PNG en el blanco roto del texto. */
:root[data-tema="oscuro"] .gb-logo-panel{filter:brightness(0) invert(0.9);}
/* De noche el campo relleno (el buscador de las carpetas) lleva el filo de lo que se toca (N3): su relleno hundido es el mismo gris que la fila de herramientas y el campo desaparecía, solo quedaba el texto (2026-09-18, el dueño: "el buscador no tiene límites"). */
:root[data-tema="oscuro"] .gb-field-soft{border-color:var(--gb-border);}
[data-gb-nivel]{position:relative;isolation:isolate;--gb-nivel-marco:12px;}
[data-gb-nivel]::before{content:"";position:absolute;inset:var(--gb-nivel-marco);border-radius:18px;background:var(--gb-surface);pointer-events:none;z-index:-1;}
[data-gb-nivel]::after{content:"";position:absolute;inset:var(--gb-nivel-marco);border-radius:18px;border:1px solid var(--gb-widgetBorde);box-shadow:0 0 0 var(--gb-nivel-marco) var(--gb-bg);pointer-events:none;z-index:1;}
/* Un nivel que muestra una hoja no lleva marco (2026-09-18, el dueño sobre el presupuesto abierto de noche: "el fondo y paisaje y hoja tienen un nivel intermedio... me gustaría que solo fuera la hoja y el fondo como en el claro"): la hoja va directo sobre el fondo. Los demás niveles llevan su marco de día y de noche (mismo día: "entonces aplícalo también para el claro"). */
[data-gb-nivel][data-gb-nivel-hoja]{--gb-nivel-marco:0px;}
[data-gb-nivel][data-gb-nivel-hoja]::before,[data-gb-nivel][data-gb-nivel-hoja]::after{content:none;}
/* El teléfono en horizontal (2026-10-29, lienzo "GEOBAJA en horizontal"; el dueño: "usarla en horizontal le da más
   aire... que se puedan consultar en horizontal"): ahí el alto es lo escaso y el nivel va a sangre, sin marco. */
:root[data-alto="corto"] [data-gb-nivel]{--gb-nivel-marco:0px;}
:root[data-alto="corto"] [data-gb-nivel]::before{border-radius:0;}
:root[data-alto="corto"] [data-gb-nivel]::after{content:none;}
`;
;(function injectTema() {
  if (typeof document === "undefined" || document.getElementById("gb-tema")) return;
  const s = document.createElement("style");
  s.id = "gb-tema";
  s.textContent = CSS_TEMA;
  document.head.appendChild(s);
})();

;(function injectBase() {
  if (typeof document === "undefined" || document.getElementById("gb-base")) return;
  const s = document.createElement("style");
  s.id = "gb-base";
  s.textContent = `
body { background: ${C.bg}; color: ${C.text}; font-family: ${F.ui}; }
/* Cascaron de la app (AppShell pone gb-app en <html> mientras vive): el documento
   no hace scroll (el scroll vive en <main>) y no rebota en los limites, asi que el
   encabezado y el pie no se mueven nunca. Sin esto, al llegar al tope del scroll
   de <main> o de una lista, el gesto encadenaba al documento y macOS lo estiraba
   entero, chrome incluido: "algo suelto" (2026-09-14). */
html.gb-app, html.gb-app body { height: 100%; overflow: hidden; overscroll-behavior: none; }
/* ── Transiciones entre módulos (View Transitions API, 2026-09-14) ──
   Las zonas compartidas del cascarón llevan view-transition-name: gb-miga (el
   marco de la miga), gb-nav-activo (la pastilla de la fila activa del panel) y,
   en las carpetas, gb-pestanas / gb-subrayado / gb-accion / gb-cifras /
   gb-ventana / gb-lista / gb-barra. Al cambiar de ruta (navegarConTransicion en
   components/carpeta/transicion.js) el navegador captura el antes y el después
   de cada zona y las funde: el marco permanece, el texto se cruza y lo que
   cambia de tamaño o de lugar viaja. Ritmo: el grupo viaja en M.slow con
   easeOut; lo viejo se va en M.fast y lo nuevo llega en M.base; la lista y la
   barra llegan un paso después para que el marco aterrice primero. */
::view-transition-group(*) { animation-duration: ${M.slow}; animation-timing-function: ${M.easeOut}; }
::view-transition-old(*), ::view-transition-new(*) { animation-duration: ${M.base}; animation-timing-function: ${M.ease}; }
::view-transition-old(root), ::view-transition-new(root) { animation-duration: ${M.fast}; }
::view-transition-old(gb-miga), ::view-transition-old(gb-accion), ::view-transition-old(gb-pestanas), ::view-transition-old(gb-cifras), ::view-transition-old(gb-barra) { animation-duration: ${M.fast}; }
::view-transition-new(gb-lista) { animation-delay: 40ms; animation-fill-mode: both; }
::view-transition-new(gb-barra) { animation-delay: 60ms; animation-fill-mode: both; }
/* El subrayado y la pastilla del panel solo viajan: sin cruce de imágenes. El
   contenido de cada fila del panel (gb-nav-<id>) tiene capa propia encima de la
   pastilla para no quedar tapado durante el viaje; no se mueve, solo cruza color. */
::view-transition-old(gb-subrayado) { animation: none; opacity: 0; }
::view-transition-new(gb-subrayado) { animation: none; }
/* La transición entre módulos existe ENTRE ELEMENTO Y ELEMENTO (2026-09-15, pedido
   del dueño): cada miga, cada pestaña, cada botón de la barra y la pastilla del panel
   llevan nombre propio y una clase de transición (gb-item / gb-pastilla). El elemento
   que existe en los dos módulos se queda o viaja a su lugar nuevo (grupo en M.slow) y
   cruza su imagen (M.base); el que solo está en uno se apaga (gbItemSale) o enciende
   (gbItemEntra) en su sitio. Nada recorre el panel ni la barra: la pastilla se apaga
   en su fila y enciende en la nueva con un leve ensanche. Los marcos (miga, ventana,
   barra) cortan, y su imagen ya no incluye a los elementos con nombre propio. */
@keyframes gbSeVa { from { opacity: 1; } to { opacity: 0; } }
@keyframes gbLlega { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes gbItemSale { from { opacity: 1; } to { opacity: 0; } }
@keyframes gbItemEntra { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
@keyframes gbPastillaSale { from { opacity: 1; } to { opacity: 0; } }
@keyframes gbPastillaEntra { from { opacity: 0; transform: scaleX(0.94); } to { opacity: 1; transform: none; } }
/* Cambiar de módulo (2026-09-15/16; pedido del dueño: "una transición limpia"): el
   fundido dejaba ver las dos pantallas encimadas, y el corte seco que lo sustituyó
   parpadeaba. Mientras la transición de módulo vive, <html> lleva gb-vt-modulo
   (navegarConTransicion con la opcion corte). El CONTENIDO (gb-contenido: la vista entera; y
   dentro de una carpeta la ventana, la lista, la barra, las pestañas y las cifras) hace
   FADE-THROUGH: lo que se va se apaga hasta el fondo en 90 ms y lo que llega enciende
   después, en 150 ms, subiendo 4 px; las dos pantallas nunca se ven a la vez ni cambian
   de golpe. El cascarón (panel, barra superior, pie) es idéntico y no se percibe; la
   miga muta su marco; la ventana y la barra mueven su marco cuando las cifras entran o
   salen (la línea de cifras empuja la ventana). Abrir un documento desde su miniatura
   (conTransicionDeVista, y navegar sin la opcion corte) conserva su coreografía completa. */
html.gb-vt-modulo::view-transition-group(*) { animation-duration: 0s; }
html.gb-vt-modulo::view-transition-old(*), html.gb-vt-modulo::view-transition-new(*) { animation-duration: 0s; animation-delay: 0s; }
html.gb-vt-modulo::view-transition-old(gb-contenido), html.gb-vt-modulo::view-transition-old(gb-lista), html.gb-vt-modulo::view-transition-old(gb-cifras) { animation: gbSeVa 90ms ${M.ease} both; }
html.gb-vt-modulo::view-transition-new(gb-contenido), html.gb-vt-modulo::view-transition-new(gb-lista), html.gb-vt-modulo::view-transition-new(gb-cifras) { animation: gbLlega 150ms ${M.easeOut} 90ms both; }
/* El piso del fade-through: mientras lo viejo ya se fue y lo nuevo aún no llega, el área del
   contenido muestra el fondo (sin él, el navegador deja ver el DOM nuevo debajo y el fundido
   se convierte en un corte). */
html.gb-vt-modulo::view-transition-group(gb-contenido) { background: ${C.bg}; }
/* Los marcos de la carpeta (ventana, barra, fila de pestañas) permanecen: su imagen cruza y su
   caja muta si cambia de lugar; lo que fade-through es lo que llevan dentro. */
html.gb-vt-modulo::view-transition-old(gb-ventana), html.gb-vt-modulo::view-transition-old(gb-barra), html.gb-vt-modulo::view-transition-old(gb-pestanas) { animation-duration: ${M.fast}; }
html.gb-vt-modulo::view-transition-new(gb-ventana), html.gb-vt-modulo::view-transition-new(gb-barra), html.gb-vt-modulo::view-transition-new(gb-pestanas) { animation-duration: ${M.base}; }
html.gb-vt-modulo::view-transition-group(gb-ventana), html.gb-vt-modulo::view-transition-group(gb-lista), html.gb-vt-modulo::view-transition-group(gb-barra), html.gb-vt-modulo::view-transition-group(gb-pestanas) { animation-duration: ${M.slow}; animation-timing-function: ${M.easeOut}; }
html.gb-vt-modulo::view-transition-group(gb-miga) { animation-duration: ${M.slow}; }
html.gb-vt-modulo::view-transition-old(gb-miga) { animation-duration: ${M.fast}; }
html.gb-vt-modulo::view-transition-new(gb-miga) { animation-duration: ${M.base}; }
/* Los elementos con nombre (migas, pestañas, botones, cifras, acción): el que existe en los dos
   módulos se queda o viaja y cruza su imagen; el que solo está en uno se apaga con el contenido
   o enciende con él. El par de imágenes se recorta al grupo: lo que crece se revela, no se encima. */
html.gb-vt-modulo::view-transition-group(.gb-item) { animation-duration: ${M.slow}; animation-timing-function: ${M.easeOut}; }
html.gb-vt-modulo::view-transition-image-pair(.gb-item) { overflow: clip; }
html.gb-vt-modulo::view-transition-old(.gb-item), html.gb-vt-modulo::view-transition-new(.gb-item) { animation-duration: ${M.base}; animation-timing-function: ${M.ease}; }
html.gb-vt-modulo::view-transition-old(.gb-item):only-child { animation: gbItemSale 90ms ${M.ease} both; }
html.gb-vt-modulo::view-transition-new(.gb-item):only-child { animation: gbItemEntra 150ms ${M.easeOut} 90ms both; }
/* El GROSOR de un elemento no cambia al viajar (2026-09-16, pedido del dueño): la
   imagen capturada de un botón, una miga o una pestaña NO se estira con su grupo
   (por defecto el navegador la escala al ancho y con ella crece el alto); queda a su
   tamaño, anclada a la izquierda, y el grupo solo cambia de largo con el texto que
   contiene. Lo anclado a la derecha (las puertas de la barra) crece hacia la izquierda. */
html.gb-vt-modulo::view-transition-old(.gb-item), html.gb-vt-modulo::view-transition-new(.gb-item) { block-size: 100%; object-fit: none; object-position: left center; }
html.gb-vt-modulo::view-transition-old(.gb-derecha), html.gb-vt-modulo::view-transition-new(.gb-derecha) { object-position: right center; }
html.gb-vt-modulo::view-transition-group(.gb-pastilla) { z-index: -1; animation: none; }
html.gb-vt-modulo::view-transition-old(.gb-pastilla) { animation: gbPastillaSale ${M.fast} ${M.ease} both; }
html.gb-vt-modulo::view-transition-new(.gb-pastilla) { animation: gbPastillaEntra ${M.base} ${M.easeOut} both; }
/* El orden de las capas NO puede depender del orden de captura (2026-09-15).
   El navegador apila los grupos en el orden en que los capturó, que es el orden
   de pintado del ANTES, y lo que solo existe en el DESPUÉS va al final, encima de
   todo: una pastilla que enciende en su fila taparía el texto de esa fila mientras
   aparece y el botón se vería en blanco. Por eso la instantánea de la página va al
   fondo (-2), las pastillas encima de ella (-1, reglas de SidebarNav) y todo lo
   demás, las filas incluidas, encima de las pastillas. */
::view-transition-group(root) { z-index: -2; }
/* Texto anclado: la acción crece desde su borde derecho; las pestañas y las
   cifras, desde el izquierdo. La imagen capturada no se estira. */
::view-transition-old(gb-accion), ::view-transition-new(gb-accion) { block-size: 100%; object-fit: none; object-position: right center; }
::view-transition-old(gb-pestanas), ::view-transition-new(gb-pestanas), ::view-transition-old(gb-cifras), ::view-transition-new(gb-cifras) { block-size: 100%; object-fit: none; object-position: left center; }
@media (prefers-reduced-motion: reduce) {
  ::view-transition-group(*), ::view-transition-old(*), ::view-transition-new(*) { animation: none !important; }
}
button, input, select, textarea { font-family: inherit; }
::selection { background: ${C.primaryMid}; color: ${C.primaryDeep}; }
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: ${R.xs}px; }
* { scrollbar-width: thin; scrollbar-color: ${C.borderMid} transparent; }

/* ── Números tabulares: montos, cantidades y folios alinean por columna ── */
.num-tabular, td.num, th.num { font-variant-numeric: tabular-nums; font-feature-settings: "tnum"; }

/* ── Focus visible: una sola convención (C.focus, azul de marca + resplandor) ── */
*:focus { outline: none; }
*:focus-visible { outline: 2px solid ${C.focus}; outline-offset: 2px; border-radius: ${R.xs}px; }
button:focus-visible, a:focus-visible { outline: 2px solid ${C.focus}; outline-offset: 2px; }
input:focus-visible, select:focus-visible, textarea:focus-visible {
  outline: 2px solid ${C.focus}; outline-offset: 1px;
  border-color: ${C.focus} !important;
  box-shadow: ${E.focusInput};
}
.gb-focus:focus-visible { outline: none; box-shadow: ${E.focus}; }
/* Campo relleno (buscadores y filtros de panel, 2026-09-09): fondo surfaceSunken sin
   borde visible y radio R.lg, la excepción documentada al input bordeado de radio R.ctl.
   Al enfocarse vuelve a blanco; el borde y el resplandor los pone la regla de arriba. */
.gb-field-soft{background:${C.surfaceSunken};border:1px solid transparent;border-radius:${R.lg}px;}
.gb-field-soft:focus-visible{background:${C.surface};}

/* ── Estados interactivos ── */
.gb-i{transition:background-color ${M.fast} ${M.ease},border-color ${M.fast} ${M.ease},box-shadow ${M.base} ${M.ease},transform ${M.fast} ${M.ease},color ${M.fast} ${M.ease},opacity ${M.fast} ${M.ease};}
.gb-press{cursor:pointer;-webkit-tap-highlight-color:transparent;}
/* Press = confirmación sensorial: el anillo entra en 0ms (snap) y al soltar se
   desvanece con la transición de gb-i. Sin transform: luz, no hundimiento. La
   burbuja y sus menús tienen su propio toque (Tinta, más abajo): sin anillo. */
.gb-press:active:not(:disabled):not([aria-disabled="true"]){box-shadow:${E.press};transition-duration:0ms;}
/* Una TARJETA que entra (gb-lift: servicios, contratos, la miniatura; gb-tarjeta: la que no se eleva, como los
   widgets del Inicio) no se enciende con el anillo (2026-10-25, el
   dueño: "al dar click en la tarjeta del servicio aparece un color de fondo azul en el botón que se ve mal"; el halo
   además quedaba congelado en la transición de vista): al pulsarla baja a su lugar. El anillo es de los botones. */
.gb-press.gb-lift:active:not(:disabled):not([aria-disabled="true"]),
.gb-press.gb-tarjeta:active:not(:disabled):not([aria-disabled="true"]){box-shadow:${E.e1};transform:none;transition-duration:60ms;}
.gb-press:disabled,.gb-press[aria-disabled="true"]{cursor:not-allowed;}
.gb-row{cursor:pointer;}
/* Lo que asoma al señalar una fila (el lápiz de un dato que se edita): en reposo
   la fila es solo su dato; al pasar el cursor o con el foco del teclado, aparece.
   Sin cursor (el teléfono) queda siempre a la vista: con el dedo no se señala.
   Todo :hover vive en el bloque (hover: hover) de abajo (2026-10-12). */
.gb-row .gb-asoma{opacity:0;transition:opacity ${M.fast} ${M.ease};}
.gb-row:focus-visible .gb-asoma{opacity:1;}
/* Enlace a ficha de entidad (EntityLinks). Comunica EN REPOSO, por color: el
   punteado anterior solo existia al pasar el cursor, asi que un nombre
   navegable era indistinguible de texto muerto hasta que alguien lo adivinaba
   (y para adivinarlo habia que sospecharlo antes). El hover enciende un pozo
   tenue en vez de una raya, y el estado vive aqui, no en un onMouseEnter. */
.gb-entity{color:${C.primary};cursor:pointer;border-radius:${R.xs}px;padding:0 3px;margin:0 -3px;text-decoration:none;-webkit-box-decoration-break:clone;box-decoration-break:clone;}
.gb-entity:focus-visible{background:${C.primaryLight};color:${C.primaryDark};}
.gb-alt{background:${C.tableAlt};}
/* Zebra por prop <Table striped>: fuente única, sin checks ei%2 inline (v4). */
.gb-striped tbody tr:nth-of-type(even) td{background:${C.tableAlt};}
.gb-lift{cursor:pointer;}
/* Reportes (2026-10-17): la hoja de cada reporte en Íconos se alza al señalarla (el hover vive abajo). */
.gb-reporte .gb-reporte-hoja{transition:transform ${M.base} ${M.easeOut},box-shadow ${M.fast} ${M.ease};}
/* Lenguaje EN CALMA (2026-09-17, el dueño: "los botones y las cajas de los mismos
   se podrían rediseñar para el calma que es más circular"): dentro de una zona
   .gb-calma —la fila de herramientas y la barra de estado de una carpeta— lo que
   se toca es una PASTILLA, igual que en la burbuja de acciones. Va por clase y no
   por prop para que ningún módulo tenga que recolocar nada: el botón conserva su
   alto, su relleno y su tipografía, solo cambia la esquina. */
.gb-calma button, .gb-calma .gb-btn{border-radius:${R.pill}px !important;}
.gb-calma input[type="text"]{border-radius:${R.pill}px !important;}
/* El kebab y los cuadrados de icono siguen siendo círculo perfecto por su propio radio. */

/* La burbuja de acciones (Cinta.js, 2026-09-15): sus botones son pastilla y el
   hover TIÑE icono y rótulo de primary en vez de pintar una caja (la caja
   cuadrada rompía la estética de la pastilla); lo activo y el menú abierto
   conservan su relleno claro. Decisión del dueño sobre el lienzo
   "Burbuja de acciones". */
.gb-burbuja .gb-cinta-boton{border-radius:${R.pill}px !important;}
/* Lo que se teclea en la burbuja también es cápsula (2026-11-03, el dueño sobre la fecha de la bitácora de
   acarreos: "en la burbuja el recuadro de ingreso de fecha no corresponde, debería de ser en forma de cápsula"):
   el campo relleno de la calma (gb-field-soft, DateInput soft) con radio de pastilla, como sus botones. */
.gb-burbuja .gb-field-soft{border-radius:${R.pill}px !important;}
/* El toque de la burbuja, B1 Tinta (2026-10-02, lienzo "Burbuja metamorfa"; el dueño:
   "se siente lenta, tiene que ser responsiva, es un metamorfo al fin de cuentas"):
   al pulsar, icono y rótulo se encogen al 90% y se tiñen de primary en 60 ms, sin
   nada detrás ni anillo; al soltar rebotan en 260 ms. La primaria se encoge y su
   relleno se oscurece. El foco de teclado conserva su anillo. Vale también en los
   menús que salen de la burbuja. */
.gb-burbuja .gb-cinta-in{transition:transform 260ms cubic-bezier(.34,1.56,.64,1);}
.gb-burbuja .gb-cinta-in svg,.gb-burbuja .gb-cinta-in span{transition:color 60ms ${M.ease},stroke 60ms ${M.ease};}
.gb-burbuja .gb-cinta-boton:active:not(:disabled) .gb-cinta-in{transform:scale(.9);transition-duration:60ms;transition-timing-function:cubic-bezier(.2,.7,.2,1);}
.gb-burbuja .gb-press:active:not(:disabled):not(:focus-visible),.gb-menu-burbuja .gb-press:active:not(:disabled):not(:focus-visible){box-shadow:none;}
.gb-burbuja .gb-cinta-boton.gb-ghost:active:not(:disabled) svg,.gb-burbuja .gb-cinta-boton[data-en-camino] svg{stroke:${C.primary};}
.gb-burbuja .gb-cinta-boton.gb-ghost:active:not(:disabled) span,.gb-burbuja .gb-cinta-boton[data-en-camino] span{color:${C.primary} !important;}
.gb-burbuja .gb-cinta-boton.gb-fill-primary:active:not(:disabled){background:${C.primaryDark} !important;}
/* Lo demás que se pulsa en la burbuja (zoom, Eliminar) se encoge sin teñirse (Eliminar
   conserva su rojo) y el renglón de un menú de la burbuja tiñe su icono y su rótulo. */
.gb-burbuja .gb-press:not(.gb-cinta-boton)>*{transition:transform 260ms cubic-bezier(.34,1.56,.64,1);}
.gb-burbuja .gb-press:not(.gb-cinta-boton):active:not(:disabled)>*{transform:scale(.86);transition-duration:60ms;transition-timing-function:cubic-bezier(.2,.7,.2,1);}
.gb-menu-burbuja .gb-press:active:not(:disabled)>svg{stroke:${C.primary};}
.gb-menu-burbuja .gb-press:active:not(:disabled)>span>span:first-child{color:${C.primary} !important;}
/* El metamorfo (Cinta.js): la puerta que se suelta queda en camino, teñida y sola junto
   a Volver; lo demás se pliega en su lugar en 200 ms. Al llegar la página, lo suyo
   brota. Mientras tanto la pastilla sigue a su contenido sin transición propia (si
   persiguiera cada medida, llegaría tarde) y con el ancho exacto de su medida: el
   relleno de 12 px menos el borde de 1 px de todas las pieles, que la medida ya lleva
   dentro. Solo el contenido interpola desde auto: si la pastilla lo hiciera, al
   soltar el modo partiría de un auto sin relleno y se encogería un instante. Se elige
   por POSICIÓN, no por clase, para que se pliegue lo que cada vista ponga. */
.gb-burbuja-contenido{interpolate-size:allow-keywords;}
.gb-burbuja[data-metamorfo]{width:auto !important;padding:0 11px !important;transition:none !important;}
.gb-burbuja-contenido[data-plegando] .gb-burbuja-fila>:not(.gb-ranura):not([data-en-camino]):not([data-volver]),
.gb-burbuja-contenido[data-plegando] .gb-ranura>*{width:0 !important;min-width:0 !important;padding-left:0 !important;padding-right:0 !important;margin-left:-1px !important;margin-right:-1px !important;opacity:0;overflow:hidden;pointer-events:none;transition:width 200ms cubic-bezier(.2,.9,.25,1),min-width 200ms cubic-bezier(.2,.9,.25,1),padding 200ms cubic-bezier(.2,.9,.25,1),margin 200ms cubic-bezier(.2,.9,.25,1),opacity 110ms ease;}
.gb-burbuja-contenido[data-plegando] .gb-burbuja-der{width:0 !important;margin-left:-10px !important;opacity:0;overflow:hidden;pointer-events:none;transition:width 200ms cubic-bezier(.2,.9,.25,1),margin 200ms cubic-bezier(.2,.9,.25,1),opacity 110ms ease;}
@keyframes gbBrota{from{width:0;min-width:0;padding-left:0;padding-right:0;margin-left:-1px;margin-right:-1px;opacity:0}}
@keyframes gbBrotaDer{from{width:0;margin-left:-10px;opacity:0}}
.gb-burbuja-contenido[data-brotando] .gb-burbuja-fila>:not(.gb-ranura):not([data-volver]),
.gb-burbuja-contenido[data-brotando] .gb-ranura>*{overflow:hidden;animation:gbBrota 200ms cubic-bezier(.2,.9,.25,1) both;}
.gb-burbuja-contenido[data-brotando] .gb-burbuja-der{overflow:hidden;animation:gbBrotaDer 200ms cubic-bezier(.2,.9,.25,1) both;}
/* La burbuja no lleva leyendas de grupo (2026-09-18, el dueño al ver "VISTA" bajo
   las herramientas del Programa: "es el único lugar en donde la burbuja tiene
   etiqueta de texto para agrupar acciones y por eso está un poco más ancha").
   GrupoCinta la sigue dibujando en la cinta clásica (Planeación, el teléfono). */
.gb-burbuja .gb-cinta-grupo-titulo{display:none;}
/* ── El teléfono en horizontal (2026-10-29, lienzo "GEOBAJA en horizontal", B: "se recoge al leer"). Con
   <html data-alto="corto"> (utils.js, MQ_ALTO_CORTO) lo que flota se compacta: la burbuja en un renglón de
   botones de 36 (icono sobre rótulo, como siempre) y lo que no cabe tras Más (Cinta.js); la barra a 34 y a 6
   del borde. Al bajar leyendo, la burbuja y la barra se recogen y vuelven al subir (data-gb-recogido en el
   nivel, Cinta.js). Lo que brota conserva su animación: el mínimo compacto no entra mientras brota. */
html[data-alto="corto"] .gb-burbuja .gb-cinta-boton{padding-top:4px !important;padding-bottom:3px !important;}
html[data-alto="corto"] .gb-burbuja-contenido:not([data-brotando]) .gb-cinta-boton{min-width:50px !important;padding-left:7px !important;padding-right:7px !important;}
html[data-alto="corto"] .gb-burbuja .gb-cinta-in{gap:2px !important;}
html[data-alto="corto"] .gb-burbuja .gb-sep-cinta{margin:2px 3px !important;}
html[data-alto="corto"] .gb-burbuja[data-metamorfo]{padding:3px 11px 0 !important;}
html[data-alto="corto"] .gb-burbuja-marco{transition:transform ${M.slow} ${M.easeOut},opacity ${M.slow} ${M.easeOut};}
html[data-alto="corto"] [data-gb-recogido]>.gb-burbuja-marco{transform:translateY(calc(-100% - 12px));opacity:0;pointer-events:none;}
html[data-alto="corto"] [data-gb-pegado="abajo"]{height:34px !important;bottom:max(6px, calc(env(safe-area-inset-bottom, 0px) - 4px)) !important;transition:transform ${M.slow} ${M.easeOut},opacity ${M.slow} ${M.easeOut};}
html[data-alto="corto"] [data-gb-recogido] [data-gb-pegado="abajo"]{transform:translateY(calc(100% + 12px));opacity:0;pointer-events:none;}
/* Los hovers de gb-ghost, gb-fill-*, gb-btn-soft, gb-tint, gb-tint-critical, gb-dim, gb-handle, gb-asa,
   gb-tecla-fila y gb-th-sort viven en el bloque (hover: hover) de abajo (2026-10-12): en iOS un toque enciende
   el :hover y lo deja pegado. Aquí queda lo que no depende del cursor. */
/* Asa de redimension (sidebar de la libreta): se enciende al hover y mientras se arrastra (.is-active). */
.gb-handle{background:${C.border};transition:background ${M.fast} ${M.ease},box-shadow ${M.fast} ${M.ease};}
.gb-handle.is-active{background:${C.primaryMid};box-shadow:0 0 0 1px ${C.primaryMid};}
/* Asa en el aire entre dos recuadros (la lista suelta de la libreta de compactaciones y su hoja, 2026-10-08):
   sin raya en reposo; al señalarla (abajo, bajo hover) o arrastrarla (.is-active) se enciende una al centro. */
.gb-asa{position:relative;cursor:col-resize;}
.gb-asa::after{content:"";position:absolute;top:18px;bottom:18px;left:50%;width:2px;margin-left:-1px;border-radius:2px;background:transparent;transition:background ${M.fast} ${M.ease};}
.gb-asa.is-active::after{background:${C.primaryMid};}
/* Asa de una columna de la lista de una carpeta (2026-10-24): el borde del encabezado que se arrastra para
   ajustar el ancho. Media asa a cada lado de la línea (ListaCarpeta); invisible en reposo, la raya del lado de
   la línea se enciende al señalarla y mientras se arrastra (.is-active). La misma asa toma el borde izquierdo
   de la vista previa (Carpeta, 2026-10-27). */
.gb-asa-columna::after{content:"";position:absolute;top:7px;bottom:7px;width:2px;border-radius:1px;background:transparent;transition:background ${M.fast} ${M.ease};}
.gb-asa-columna[data-lado="derecha"]::after{right:0;}
.gb-asa-columna[data-lado="izquierda"]::after{left:0;}
.gb-asa-columna.is-active::after{background:${C.primaryMid};}
/* Tabla (Obj. C): header de columna ordenable */
.gb-th-sort{cursor:pointer;}
/* Modales (Obj. D): entrada scrim (fade) + panel (sube/escala); el bloque reduced-motion los anula */
.gb-modal-scrim{animation:gbFade ${M.base} ${M.ease};}
.gb-modal-panel{animation:gbModalIn ${M.base} ${M.easeOut};}
/* Salida de modal (Obj. F): cierre fluido cuando se marca .is-closing */
.gb-modal-scrim.is-closing{animation:gbFadeOut ${M.fast} ${M.ease} forwards;}
.gb-modal-panel.is-closing{animation:gbModalOut ${M.fast} ${M.ease} forwards;}
/* Entrada de contenido (Obj. F): gb-rise simple; gb-stagger escalona hijos directos (paso M.stagger) */
.gb-rise{animation:gbRise ${M.base} ${M.easeOut} both;}
.gb-stagger>*{animation:gbRise ${M.base} ${M.easeOut} both;}
.gb-stagger>*:nth-child(1){animation-delay:0ms;}
.gb-stagger>*:nth-child(2){animation-delay:${M.stagger};}
.gb-stagger>*:nth-child(3){animation-delay:calc(${M.stagger}*2);}
.gb-stagger>*:nth-child(4){animation-delay:calc(${M.stagger}*3);}
.gb-stagger>*:nth-child(5){animation-delay:calc(${M.stagger}*4);}
.gb-stagger>*:nth-child(6){animation-delay:calc(${M.stagger}*5);}
.gb-stagger>*:nth-child(7){animation-delay:calc(${M.stagger}*6);}
.gb-stagger>*:nth-child(8){animation-delay:calc(${M.stagger}*7);}
.gb-stagger>*:nth-child(n+9){animation-delay:calc(${M.stagger}*8);}
@media (prefers-reduced-motion: reduce){.gb-stagger>*{animation:none !important;}}
/* Barra de progreso que "se carga" (Obj. F): el fill crece de 0 desde la izquierda */
.gb-bar-fill{transform-origin:left;animation:gbBarFill .6s ${M.easeOut} both;}
/* Acordeón real (Obj. F): wrapper SIEMPRE montado que colapsa via grid-template-rows; abre Y cierra animado.
   Uso: <div className={"gb-expand"+(open?" is-open":"")}><div>…contenido…</div></div>. El padding/margin va DENTRO del hijo. */
.gb-expand{display:grid;grid-template-rows:0fr;transition:grid-template-rows ${M.base} ${M.easeOut};}
.gb-expand.is-open{grid-template-rows:1fr;}
.gb-expand>*{overflow:hidden;min-height:0;opacity:0;transition:opacity ${M.fast} ${M.ease};}
.gb-expand.is-open>*{opacity:1;}
@media (prefers-reduced-motion: reduce){.gb-expand{transition:none;}.gb-expand>*{opacity:1;transition:none;}}
/* El panel suelto (2026-10-01): el panel y lo que se recorre con él (el área de trabajo, la carpeta, el Inicio y la
   zona del logo de la barra superior) se mueven en el mismo cuadro. Solo después de la primera pintura (el panel pone
   data-gb-panel-anima): la pantalla con la que se entra ya sale en su lugar. Ver shell/SidebarNav.js. */
html[data-gb-panel-anima] .gb-panel-suelto{transition:transform ${M.slow} ${M.ease},opacity ${M.slow} ${M.ease},box-shadow ${M.slow} ${M.ease};}
html[data-gb-panel-anima] .gb-sigue-panel{transition:padding-left ${M.slow} ${M.ease},margin-left ${M.slow} ${M.ease},left ${M.slow} ${M.ease},width ${M.slow} ${M.ease};}
@media (prefers-reduced-motion: reduce){html[data-gb-panel-anima] .gb-panel-suelto,html[data-gb-panel-anima] .gb-sigue-panel{transition:none;}}
.gb-resolve{animation:gbResolve .8s ease;}
@keyframes gbResolve{0%{background:${C.warningLight}}45%{background:${C.successLight}}100%{background:transparent}}

/* ── Luz de marca: barrido sutil en hover de acciones primarias ── */
.gb-shine{position:relative;overflow:hidden;}
.gb-shine::after{content:"";position:absolute;top:0;bottom:0;left:0;width:60%;pointer-events:none;background:linear-gradient(105deg,transparent 0%,${C.shine} 50%,transparent 100%);transform:translateX(-140%);}

/* ── Skeleton: respira con el mismo barrido del LogoLoader (1.4s lineal) ── */
.gb-skel{position:relative;overflow:hidden;background:${C.borderLight};border-radius:${R.sm}px;}
.gb-skel::after{content:"";position:absolute;inset:0;background:linear-gradient(100deg,transparent 20%,${C.skelSheen} 50%,transparent 80%);transform:translateX(-100%);animation:gbShimmer 1.4s linear infinite;}
@keyframes gbShimmer{to{transform:translateX(100%)}}

/* ── Marquee "pasarela": en hover revela el texto que no cabe. Solo transform
   (GPU/compositor) y solo el elemento bajo el cursor; sin JS ni medición. El
   ancho del recorrido sale de unidades de container-query (100cqw - 100%). ── */
.gb-marquee{display:block;overflow:hidden;max-width:100%;container-type:inline-size;}
.gb-marquee-i{display:inline-block;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;vertical-align:bottom;}
@keyframes gbMarquee{0%,14%{transform:translateX(0)}86%,100%{transform:translateX(min(0px,calc(100cqw - 100%)))}}

/* ── Keyframes compartidos ── */
@keyframes gbSpin{to{transform:rotate(360deg)}}
.gb-spin{display:inline-block;border-radius:${R.round};border-style:solid;border-color:currentColor;border-right-color:transparent;animation:gbSpin .62s linear infinite;}
@keyframes gbPopDown{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
@keyframes gbDesdeBurbuja{from{opacity:0;transform:translateY(-6px) scale(.97)}to{opacity:1;transform:none}}
@keyframes gbPopUp{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}

/* ── Chat: puntos de "escribiendo…" (estilo iMessage), barra de reacciones ── */
.gb-typing-dot{display:inline-block;width:6px;height:6px;border-radius:50%;background:${C.textMuted};animation:gbTyping 1.25s infinite ease-in-out;}
@keyframes gbTyping{0%,75%,100%{opacity:.35;transform:translateY(0)}38%{opacity:.95;transform:translateY(-3px)}}
/* Focus silencioso (composer del chat): sin anillo teal; solo el borde se afirma. */
.gb-focus-quiet:focus-visible{outline:none;border-color:${C.borderMid} !important;box-shadow:none;}
/* Aviso de mensaje nuevo en el botón de chat: halo que respira 2 veces y muere. */
.gb-chat-ping{animation:gbChatPing 1.1s ${M.ease} 2;}
@keyframes gbChatPing{0%,100%{box-shadow:none}40%{box-shadow:${E.glowPrimary}}}
/* Llegada de un aviso (versión nueva): el MISMO halo del chat, sin el nombre
   del chat. Respira dos veces y muere: avisa una vez y se queda esperando. */
.gb-ping{animation:gbChatPing 1.1s ${M.ease} 2;}
/* Pieza que trabaja: el icono que invitaba a pulsar es el que ahora gira. */
.gb-turn{animation:gbSpin .9s linear infinite;}
/* Carga indeterminada: el bloque viaja porque no se sabe cuánto falta, y
   fingir un porcentaje sería inventarlo. */
.gb-bar-indet{position:relative;height:2px;background:${C.primaryLight};overflow:hidden;}
.gb-bar-indet::after{content:"";position:absolute;top:0;bottom:0;width:40%;background:${C.primary};animation:gbBarIndet 1.15s ${M.ease} infinite;}
@keyframes gbBarIndet{0%{left:-40%}100%{left:100%}}
@keyframes gbScaleIn{from{opacity:0;transform:scale(.82)}to{opacity:1;transform:none}}
@keyframes gbModalIn{from{opacity:0;transform:translateY(8px) scale(.985)}to{opacity:1;transform:none}}
@keyframes gbFade{from{opacity:0}to{opacity:1}}
/* Invitación a tocar (2026-09-14): un anillo de marca que se expande y se
   desvanece tres veces, lento, y después descansa; para la insignia de la
   hoja compacta de la estimación (tocarla la amplía). Nunca hostiga: tres
   latidos y silencio; con "reducir movimiento" no late. */
@keyframes gbInvita{0%{box-shadow:0 0 0 0 color-mix(in srgb, ${C.primary} 35%, transparent)}70%{box-shadow:0 0 0 10px transparent}100%{box-shadow:0 0 0 0 transparent}}
.gb-invita{animation:gbInvita 2.6s cubic-bezier(.2,.7,.2,1) 0.8s 3 both;}
@keyframes gbRise{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@keyframes gbSheetUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
@keyframes gbModalOut{from{opacity:1;transform:none}to{opacity:0;transform:translateY(6px) scale(.985)}}
@keyframes gbFadeOut{from{opacity:1}to{opacity:0}}
@keyframes gbBarFill{from{transform:scaleX(0)}to{transform:scaleX(1)}}

/* ── El hover, solo con cursor (2026-10-12, lienzo "GEOBAJA en el iPhone", dirección A, elegida por el
   dueño: "Implementa A"). En iOS un toque enciende el :hover y lo deja pegado hasta el siguiente toque:
   la fila quedaba teñida. TODO hover de la hoja vive aquí, bajo (hover: hover); sus mitades de foco
   (:focus-visible) siguen arriba, donde cada regla. Un hover nuevo se agrega aquí, nunca suelto. */
@media (hover: hover) {
.gb-row:hover{background:${C.tableHover};}
.gb-row:hover .gb-asoma{opacity:1;}
.gb-entity:hover{background:${C.primaryLight};color:${C.primaryDark};}
.gb-carril:hover{background:${C.tableHover};}
.gb-carril:hover>*:first-child{background:${C.tableHover};}
.gb-lift:hover{box-shadow:${E.e2};transform:translateY(-1px);}
.gb-reporte:hover .gb-reporte-hoja{transform:translateY(-2px);}
.gb-burbuja .gb-cinta-boton.gb-ghost:not([data-activo]):hover:not(:disabled){background:transparent !important;}
.gb-burbuja .gb-cinta-boton[data-activo]:hover:not(:disabled){background:${C.primaryLight} !important;}
.gb-burbuja .gb-cinta-boton.gb-ghost:hover:not(:disabled) svg{stroke:${C.primary};}
.gb-burbuja .gb-cinta-boton.gb-ghost:hover:not(:disabled) span{color:${C.primary} !important;}
.gb-ghost:hover:not(:disabled){background:${C.tableHover} !important;}
/* Hover de botones (Obj. B): relleno se oscurece, no-relleno se tiñe. !important
   para ganarle al background inline de cada variante (igual criterio que gb-ghost). */
.gb-fill-primary:hover:not(:disabled){background:${C.primaryDark} !important;}
.gb-fill-critical:hover:not(:disabled){background:${C.criticalDark} !important;}
.gb-btn-soft:hover:not(:disabled){background:${C.surfaceSunken} !important;border-color:${C.borderMid} !important;}
/* Tinte de marca al hover (barra superior, chips de filtro, tarjetas de accion): fondo
   primaryLight, borde y texto primary. Sustituye a los onMouseEnter que pintaban eso a mano. */
.gb-tint:hover:not(:disabled){background:${C.primaryLight} !important;border-color:${C.primary} !important;color:${C.primary} !important;}
/* Tinte destructivo (quitar, desactivar, cerrar sesion): misma familia critical que Btn danger. */
.gb-tint-critical:hover:not(:disabled){background:${C.criticalLight} !important;border-color:${C.criticalBorder} !important;color:${C.critical} !important;}
/* Chip con relleno suave (primaryLight, accentLight...) que se oscurece un paso al hover, sea del color que sea. */
.gb-dim:hover:not(:disabled){filter:brightness(.94);}
.gb-handle:hover{background:${C.primaryMid};box-shadow:0 0 0 1px ${C.primaryMid};}
.gb-asa:hover::after{background:${C.primaryMid};}
.gb-asa-columna:hover::after{background:${C.primaryMid};}
/* Fila dentro de una tecla (la lista suelta de la libreta de compactaciones): al señalarla, el tinte del panel. */
.gb-tecla-fila:hover{background:${C.panelHover};}
.gb-th-sort:hover{color:${C.textMed};}
.gb-shine:hover::after{transform:translateX(280%);transition:transform 600ms ${M.ease};}
.gb-marquee:hover .gb-marquee-i{max-width:none;overflow:visible;text-overflow:clip;animation:gbMarquee 3.6s ${M.ease} infinite alternate;}
}
/* ── El toque, sin cursor (misma decisión): sin anillo. Lo tocado baja a 55 % en 60 ms y vuelve al soltar
   con la transición de gb-i; una fila o un fantasma se rellenan con el hover de la tabla mientras se tocan.
   La burbuja conserva su tinta (B1). El lápiz que asoma al señalar queda siempre a la vista. */
@media (hover: none) {
.gb-press:active:not(:disabled):not([aria-disabled="true"]):not(.gb-burbuja *){box-shadow:none;opacity:.55;transition-duration:60ms;}
.gb-row:active{background:${C.tableHover};}
.gb-ghost:active:not(:disabled){background:${C.tableHover} !important;}
.gb-row .gb-asoma{opacity:1;}
}
/* ── El teléfono (2026-10-12, "GEOBAJA en el iPhone", dirección A): lo que se toca mide 44, los botones son
   cápsula, los campos van rellenos y sin borde, los estados son cápsula y el interruptor es el de iOS. Va por
   clase bajo html[data-aparato="telefono"], como gb-calma: ningún módulo recoloca nada. La escala de letra
   (17/15/13) NO entra aquí: vive en [data-gb-nativo] (CSS_TEMA) y la traen las pantallas que se dibujaron así. */
html[data-aparato="telefono"] *{-webkit-tap-highlight-color:transparent;}
html[data-aparato="telefono"] .gb-btn{min-height:44px;border-radius:${R.pill}px !important;padding:0 18px !important;font-size:15px !important;}
html[data-aparato="telefono"] .gb-btn[data-variant="secondary"],html[data-aparato="telefono"] .gb-btn[data-variant="outline"]{background:${C.surfaceSunken} !important;border-color:transparent !important;}
html[data-aparato="telefono"] .gb-boton-icono{border-color:transparent !important;}
html[data-aparato="telefono"] .gb-campo{min-height:44px;border-radius:${R.lg}px !important;border-color:transparent !important;background:${C.surfaceSunken} !important;padding:0 12px !important;}
html[data-aparato="telefono"] .gb-campo[aria-invalid="true"]{border-color:${C.errorFieldBorder} !important;background:${C.errorField} !important;}
html[data-aparato="telefono"] .gb-campo-select{min-height:44px;border-radius:${R.lg}px !important;border-color:transparent !important;background:${C.surfaceSunken} !important;}
html[data-aparato="telefono"] .gb-buscador{height:36px;border-radius:${R.lg}px !important;border-color:transparent !important;background:${C.surfaceSunken} !important;}
html[data-aparato="telefono"] .gb-estado{border-radius:${R.pill}px !important;padding:3px 9px !important;font-size:12px !important;}
html[data-aparato="telefono"] .gb-interruptor{width:51px !important;height:31px !important;}
html[data-aparato="telefono"] .gb-interruptor>span{width:27px !important;height:27px !important;}
html[data-aparato="telefono"] .gb-interruptor[aria-checked="true"]>span{left:22px !important;}

/* ── Respeto a usuarios con animaciones reducidas ── */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
  .gb-i{transition:none!important}
  .gb-lift:hover{transform:none}
  .gb-resolve{animation:none}
  .gb-shine::after{display:none}
  .gb-skel::after{animation:none}
  .gb-typing-dot{animation:none}
  .gb-marquee:hover .gb-marquee-i{max-width:100%;overflow:hidden;text-overflow:ellipsis;animation:none}
}

/* ── RESPONSIVE MÓVIL (breakpoint único 767px, espejo de useIsMobile) ── */
@media (max-width: 767px) {
  .r-grid-kpi { grid-template-columns: 1fr 1fr !important; min-width: 0; }
  .r-grid-kpi > * { min-width: 0; overflow: hidden; }
  .r-grid { grid-template-columns: 1fr !important; }
  .r-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 8px; max-width: 100%; }
  .r-table-wrap table { min-width: 480px; }
  .r-hide-mobile { display: none !important; }
  .r-only-mobile { display: revert !important; }
  .r-pad { padding: 12px !important; }
  body { overflow-x: hidden; }
  #root { overflow-x: hidden; max-width: 100vw; }
  main { overflow-x: hidden; max-width: 100vw; }
  .kpi-value { font-size: 15px !important; }
  .r-tap { min-height: 44px; }   /* lo que se toca mide 44 (2026-10-12, dirección A) */
  .r-stack-m { flex-direction: column !important; align-items: stretch !important; }
  .r-stack-m > * { width: 100% !important; }
  .r-w-full-m { width: 100% !important; max-width: 100% !important; }
  .r-grid-1-m { grid-template-columns: 1fr !important; }
  .r-no-minw-m { min-width: 0 !important; }
  /* Valor de control en telefono: 16 px, el mismo piso que los campos (iOS no
     hace zoom al enfocar). El disparador del Select es un <button> y la regla
     de index.html solo alcanza input, select y textarea. */
  .r-valor { font-size: 16px !important; }
  /* Modal full-screen opt-in en móvil (<Modal fullScreenMobile>): borde a borde. */
  .gb-modal-fs-wrap { padding: 0 !important; }
  .gb-modal-fs { max-width: 100% !important; width: 100% !important; min-height: 100dvh; max-height: 100dvh !important; border-radius: 0 !important; }
}
@media (min-width: 768px) {
  .r-only-mobile { display: none !important; }
}
`;
  document.head.appendChild(s);
})();


// ══════════════════════════════════════════════
//  IOS: LA APP DEL TELÉFONO (2026-10-30, lienzo «El estándar iOS», versión 18; DESIGN.md, «La app del teléfono»).
//  Ahí iOS manda y el canon de diseño no (el dueño, 2026-10-29). El color es el del sistema: todo se escribe con la
//  tinta, un azul marca lo que se toca y un rojo, siempre con su glifo, lo que está mal; la duda va en la tinta, en
//  negrita; la etapa se dice con su palabra (negra si te toca, gris si espera o cerró). Verde solo el interruptor.
//  Los nombres son los del lienzo (`ios.css`): la hoja `gb-ios` (src/telefono/hoja.js) los vuelve variables
//  `--<nombre>` dentro de `.gb-ios`. De día y de noche (sigue la apariencia del iPhone) y con Aumentar contraste.
//  Contraste medido en el lienzo (limpieza/contraste-resolucion.mjs): texto 4.5, marcas 3, 0 fallas.
// ══════════════════════════════════════════════
const IOS = Object.freeze({
  dia: Object.freeze({
    fondo: "#F2F2F7",                    // systemGroupedBackground
    grupo: "#FFFFFF",                    // secondarySystemGroupedBackground
    "grupo-2": "#F2F2F7",                // tertiarySystemGroupedBackground
    plano: "#FFFFFF",                    // systemBackground: redactar un correo
    texto: "#000000",                    // label: toda cifra, todo título, la etapa que te toca, la duda (600)
    "texto-2": "rgba(60,60,67,0.78)",    // secondaryLabel subido: 4.81 la peor
    "texto-3": "rgba(60,60,67,0.30)",    // tertiaryLabel: solo chevrones, hueco y próximo; nunca texto
    separador: "rgba(60,60,67,0.29)",
    relleno: "rgba(120,120,128,0.12)",   // campos, segmentado, pestaña y ficha elegidas
    "relleno-2": "rgba(120,120,128,0.20)", // pista vacía, siluetas, resalte al tocar
    acento: "#1E6EF4",                   // Blue de contraste alto: texto solo sobre --grupo y --plano (4.57)
    "acento-relleno": "#1E6EF4",         // botón lleno: blanco encima 4.57
    "sobre-acento": "#FFFFFF",
    alerta: "#BF1020",                   // palabra y glifo de problema (4.96 la peor)
    "alerta-en-relleno": "#BF1020",      // el botón destructivo de una alerta
    "alerta-relleno": "#FF383C",         // disco destructivo, insignia, tramo vencido
    gris: "#8E8E93",                     // tira estimado y espera, disco Más, círculo sin elegir
    "segmento-activo": "#FFFFFF",
    verde: "#34C759",                    // SOLO el interruptor
    vidrio: "rgba(250,250,252,0.66)",
    "vidrio-denso": "rgba(250,250,252,0.84)",
    "vidrio-filo": "inset 0 0 0 0.5px rgba(255,255,255,0.75), inset 0 1px 0 rgba(255,255,255,0.9)",
    "vidrio-sombra": "0 0 0 0.5px rgba(0,0,0,0.06), 0 6px 24px rgba(0,0,0,0.10)",
    velo: "rgba(0,0,0,0.2)",
    // La hoja completa y el correo de noche se aclaran un tono (iOS): de día son el fondo y el grupo de siempre.
    "hoja-fondo": "#F2F2F7",
    "hoja-grupo": "#FFFFFF",
    "redactar-fondo": "#FFFFFF",
  }),
  noche: Object.freeze({
    fondo: "#000000",
    grupo: "#1C1C1E",
    "grupo-2": "#2C2C2E",
    plano: "#000000",
    texto: "#FFFFFF",
    "texto-2": "rgba(235,235,245,0.62)",
    "texto-3": "rgba(235,235,245,0.30)",
    separador: "rgba(84,84,88,0.60)",
    relleno: "rgba(118,118,128,0.24)",
    "relleno-2": "rgba(120,120,128,0.36)",
    acento: "#5CB8FF",                   // Blue de contraste alto oscuro (4.95 la peor)
    "acento-relleno": "#1E6EF4",         // el botón lleno sigue en el azul de día
    "sobre-acento": "#FFFFFF",
    alerta: "#FF6165",
    "alerta-en-relleno": "#FF8485",
    "alerta-relleno": "#FF4245",
    gris: "#8E8E93",
    "segmento-activo": "#636366",        // Gray 2 oscuro: blanco encima 5.99
    verde: "#30D158",
    vidrio: "rgba(44,44,48,0.62)",
    "vidrio-denso": "rgba(36,36,40,0.86)",
    "vidrio-filo": "inset 0 0 0 0.5px rgba(255,255,255,0.14), inset 0 1px 0 rgba(255,255,255,0.10)",
    "vidrio-sombra": "0 0 0 0.5px rgba(0,0,0,0.5), 0 8px 28px rgba(0,0,0,0.45)",
    velo: "rgba(0,0,0,0.45)",
    "hoja-fondo": "#1C1C1E",
    "hoja-grupo": "#2C2C2E",
    "redactar-fondo": "#1C1C1E",
  }),
  // Aumentar contraste (prefers-contrast: more): los mismos significados a 7:1.
  contraste: Object.freeze({
    "texto-2": "rgba(60,60,67,0.93)", separador: "rgba(60,60,67,0.57)", acento: "#0D3E90", "acento-relleno": "#1553BC",
    alerta: "#950919", "alerta-en-relleno": "#950919", "alerta-relleno": "#E9152D",
  }),
  contrasteNoche: Object.freeze({
    "texto-2": "rgba(235,235,245,0.85)", separador: "rgba(235,235,245,0.38)", acento: "#B0D6FF", "acento-relleno": "#1553BC",
    alerta: "#FFC3C3", "alerta-en-relleno": "#FFC3C3", "alerta-relleno": "#FF6165",
  }),
  // Lo fijo, que no sigue al tema: el papel del PDF, su azul institucional (la portada de un documento en la galería,
  // PDF.INST) y la perilla del interruptor.
  fijo: Object.freeze({ papel: "#FFFFFF", "papel-tinta": "#000000", "papel-inst": "#2F3B52", "sombra-perilla": "rgba(0,0,0,0.18)" }),
});

// PALETAS: los valores en crudo de cada tema (la barra del navegador, pruebas).
const PALETAS = Object.freeze({ dia: C_DIA, noche: Object.freeze({ ...NOCHE }), telefono: Object.freeze({ ...TELEFONO }) });
export { C, C_DIA, PALETAS, T, T_PX, T_NATIVO, R, E, M, F, SP, Z, GLASS, LOGO_SRC, CAMPO, CAMPO_PALETAS, IOS };
